import React, { useEffect, useMemo, useRef, useState } from "react";
import "./mockup.css";
import SOPStaffGrid from "./SOPStaffGrid";
import PopupContent from "./PopupContent";
import {
  displayName,
  maskTextWithNames,
  useMaskPharmacyNames,
  MaskToggleButton,
} from "./nameMask";

/** ========== NEAR MISS SPLIT: helper formatters ========== */
const fmtDdMmYyyyHhMm = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  const pad = (n) => String(n).padStart(2, "0");
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const monthNameFromInt = (m) => {
  const names = [null, "January","February","March","April","May","June","July","August","September","October","November","December"];
  // Spec says 2 = February, so 1 = January (standard)
  return names[m] || `Month ${m}`;
};

/** ========== MONTHLY SUMMARY CARD: engine-generated amber card =========== */
function MonthlySummaryCard({ summary }) {
  const [expanded, setExpanded] = useState(false);

  const fmtPct = (n) => (n === null || n === undefined) ? "—" : `${(n * 100).toFixed(1)}%`;
  const fmtRate = (n, basis) => {
    if (n === null || n === undefined) return "—";
    if (basis === "items") return `${(n * 100).toFixed(2)}% per item`;
    if (basis === "dispenser-count") return `${n.toFixed(2)} per dispenser`;
    return Number(n).toFixed(4);
  };

  const insightCount = (summary.insights || []).length;
  const hasOutliers = summary.staffReporting && (
    (summary.staffReporting.outliersHigh?.length || 0) +
    (summary.staffReporting.outliersLow?.length || 0) > 0
  );

  return (
    <div
      style={{
        background: "#fef3c7",
        border: "1px solid #fbbf24",
        borderRadius: 8,
        padding: 12,
        marginBottom: 10,
        cursor: "pointer"
      }}
      onClick={() => setExpanded(!expanded)}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: "#78350f" }}>
          {summary.monthLabel} — engine summary
        </div>
        <div style={{ fontSize: 11, color: "#92400e" }}>
          {summary.totalIncidents} incident{summary.totalIncidents === 1 ? "" : "s"}
          {insightCount > 0 ? ` · ${insightCount} pattern${insightCount === 1 ? "" : "s"}` : ""}
          {expanded ? " ▾" : " ▸"}
        </div>
      </div>

      <div style={{ fontSize: 12, color: "#451a03", marginTop: 6, lineHeight: 1.5 }}>
        {summary.narrative}
      </div>

      {expanded && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid #fcd34d" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
            <Metric label="Incidents" value={summary.totalIncidents} />
            <Metric
              label="Rate"
              value={fmtRate(summary.rate, summary.rateBasis)}
              hint={summary.rateBasis === "dispenser-count" ? "items denominator unavailable" : null}
            />
            <Metric label="Active dispensers" value={summary.activeDispenserCount} />
          </div>

          <Section title="Reporting maturity">
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
              <Metric label="Checker populated" value={fmtPct(summary.checkerPopulated?.rate)} small />
              <Metric label="Cause completion" value={fmtPct(summary.causeCompletion?.rate)} small />
              <Metric label="LASA flag rate" value={fmtPct(summary.lasaFrequency?.rate)} small />
              <Metric
                label="Batch reporting"
                value={summary.batchReporting?.isBatch ? "Yes" : "No"}
                hint={summary.batchReporting?.isBatch
                  ? `${(summary.batchReporting.peakShare * 100).toFixed(0)}% on ${summary.batchReporting.peakDay}`
                  : null}
                small
              />
            </div>
          </Section>

          {summary.comparison?.hasPriorMonth && (
            <Section title={`vs ${summary.comparison.priorMonthLabel}`}>
              <div style={{ fontSize: 12, color: "#451a03", lineHeight: 1.6 }}>
                Incidents: {summary.comparison.priorIncidentCount} → {summary.totalIncidents}{" "}
                ({summary.comparison.incidentDelta > 0 ? "+" : ""}{summary.comparison.incidentDelta})
                {summary.comparison.rateDelta !== null && summary.comparison.rateDelta !== undefined && (
                  <> · Rate delta: {(summary.comparison.rateDelta * 100).toFixed(2)} pp</>
                )}
              </div>
            </Section>
          )}

          {summary.repeatDrugs?.repeats?.length > 0 && (
            <Section title={`Drugs recurring from prior month (${summary.repeatDrugs.repeats.length})`}>
              <ul style={{ fontSize: 12, color: "#451a03", margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                {summary.repeatDrugs.repeats.slice(0, 5).map((r, i) => (
                  <li key={i}>
                    {r.drugName} — {r.priorMonthCount} → {r.thisMonthCount}
                    {r.delta !== 0 && (
                      <span style={{ color: r.delta > 0 ? "#991b1b" : "#065f46" }}>
                        {" "}({r.delta > 0 ? "+" : ""}{r.delta})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {summary.repeatErrorTypes?.repeats?.length > 0 && (
            <Section title="Error types recurring from prior month">
              <ul style={{ fontSize: 12, color: "#451a03", margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
                {summary.repeatErrorTypes.repeats.slice(0, 5).map((r, i) => (
                  <li key={i}>
                    {r.categoryLabel} — {r.priorMonthCount} → {r.thisMonthCount}
                    {r.delta !== 0 && (
                      <span style={{ color: r.delta > 0 ? "#991b1b" : "#065f46" }}>
                        {" "}({r.delta > 0 ? "+" : ""}{r.delta})
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {hasOutliers && (
            <Section title="Staff reporting — outliers">
              {summary.staffReporting.outliersHigh?.length > 0 && (
                <div style={{ fontSize: 12, marginBottom: 6 }}>
                  <span style={{ color: "#92400e", fontWeight: 600 }}>High reporters:</span>{" "}
                  {summary.staffReporting.outliersHigh.map(o => `${o.name} (${o.count})`).join(", ")}
                </div>
              )}
              {summary.staffReporting.outliersLow?.length > 0 && (
                <div style={{ fontSize: 12 }}>
                  <span style={{ color: "#92400e", fontWeight: 600 }}>Low reporters:</span>{" "}
                  {summary.staffReporting.outliersLow.map(o => `${o.name} (${o.count})`).join(", ")}
                  <span style={{ color: "#78350f", fontStyle: "italic" }}> — supportive review, not a verdict</span>
                </div>
              )}
              <div style={{ fontSize: 11, color: "#78350f", marginTop: 4 }}>
                Branch average: {summary.staffReporting.average} per dispenser · σ {summary.staffReporting.standardDeviation}
              </div>
            </Section>
          )}

          {summary.pmiData?.hasPmis && (
            <Section title="Patient medication incidents this month">
              <div style={{ fontSize: 12, color: "#451a03", lineHeight: 1.6 }}>
                {summary.pmiData.count} PMI{summary.pmiData.count === 1 ? "" : "s"} logged
                {summary.pmiData.maxHarmLevel && summary.pmiData.maxHarmLevel !== "NO_HARM" && (
                  <> · most severe: {summary.pmiData.maxHarmLevel.toLowerCase().replace(/_/g, " ")}</>
                )}
              </div>
            </Section>
          )}

          {summary.insights?.length > 0 && (
            <Section title={`Patterns detected (${summary.insights.length})`}>
              <ul style={{ fontSize: 12, color: "#451a03", margin: 0, paddingLeft: 18, lineHeight: 1.6 }}>
                {summary.insights.map((ins, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>
                    <span style={{ fontWeight: 600 }}>{ins.title}</span>
                    <span style={{ color: "#78350f", fontSize: 10 }}> ({ins.id})</span>
                    {" — "}{ins.detail}
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4,
        color: "#78350f", marginBottom: 4
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Metric({ label, value, hint, small }) {
  return (
    <div>
      <div style={{
        fontSize: 10, fontWeight: 600, color: "#92400e",
        textTransform: "uppercase", letterSpacing: 0.3
      }}>
        {label}
      </div>
      <div style={{ fontSize: small ? 12 : 14, fontWeight: 600, color: "#451a03" }}>{value}</div>
      {hint && <div style={{ fontSize: 10, color: "#78350f", fontStyle: "italic" }}>{hint}</div>}
    </div>
  );
}

/** ========== NEAR MISS SPLIT: right panel component (on-demand fetch) ========== */
function NearMissMonthlyPanel({ pharmacyName }) {
  const [loading, setLoading] = useState(true);
  const [staffMap, setStaffMap] = useState({});
  const [reports, setReports] = useState([]);
  const [error, setError] = useState("");
  const [engineSummaries, setEngineSummaries] = useState([]);
  const [engineLoading, setEngineLoading] = useState(true);

  // Safely pick token once (no dependency on re-renders)
  const tokenRef = useRef(null);

  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
  }, []);

  useEffect(() => {
    let cancelled = false;

    async function fetchAll() {
      setLoading(true);
      setError("");

      try {
        const token = tokenRef.current;
        if (!token) throw new Error("Not authenticated. Please log in.");

        // --- Fetch monthly reports (JOIN to be done server-side in final route; for now we accept raw IDs) ---
        const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_monthly_report", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${tokenRef.current}`
          },
          body: JSON.stringify({ pharmacyName })   // <-- IMPORTANT
        });

        const text = await res.text();
        if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

        let json;
        try {
          json = JSON.parse(text);
        } catch (e) {
          throw new Error("Invalid JSON from monthly report endpoint");
        }

        // Allow various shapes: {reports: [...] } | {data: [...]} | [...]
        const arr = Array.isArray(json)
          ? json
          : Array.isArray(json.reports)
            ? json.reports
            : Array.isArray(json.data)
              ? json.data
              : [];

        // --- Optional: fetch minimal staff map if names not included in report payload ---
        const needsNames = arr.some(r =>
          (!r.pharmacistName && r.pharmacistId) ||
          (Array.isArray(r.otherStaff) && r.otherStaff.length && !r.otherStaffNames)
        );

        let staffDict = {};

        if (needsNames) {
          try {
            const resStaff = await fetch("https://api.pharmacegroup.co.uk/staffs_min", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`
              },
              body: JSON.stringify({ pharmacyName })
            });

            if (resStaff.ok) {
              const txt = await resStaff.text();

              let js;
              try {
                js = JSON.parse(txt);
              } catch {
                js = {};
              }

              const list = Array.isArray(js)
                ? js
                : Array.isArray(js.staffs)
                  ? js.staffs
                  : Array.isArray(js.data)
                    ? js.data
                    : [];

              staffDict = list.reduce((acc, s) => {
                const id = (s?._id?.$oid) || s?._id || s?.id;
                const name = s?.name || s?.fullName || s?.email || String(id || "");
                if (id) acc[id] = name;
                return acc;
              }, {});
            }
          } catch (_) {
            // non-fatal; we’ll fall back to IDs
          }
        }

        // Normalise & sort by createdAt DESC
        const norm = arr.map(r => {
          const created = r?.createdAt?.$date || r?.createdAt || r?.timestamp?.$date || r?.timestamp;
          const monthInt = r?.month;
          const year = r?.year;

          // Name resolution prefer server-provided fields if present
          let pharmacistName = r?.pharmacistName;

          if (!pharmacistName && r?.pharmacistId) {
            const pid = r.pharmacistId?.$oid || r.pharmacistId;
            pharmacistName = staffDict[pid] || pid || "";
          }

          // otherStaff could be array of ids or names
          let otherStaffNames = r?.otherStaffNames;

          if (!otherStaffNames && Array.isArray(r?.otherStaff)) {
            otherStaffNames = r.otherStaff.map(x => {
              const id = x?.$oid || x;
              return staffDict[id] || id;
            });
          }

          return {
            _raw: r,
            createdAt: created,
            createdAtMs: created ? new Date(created).getTime() : 0,
            month: Number(monthInt),
            monthName: monthNameFromInt(Number(monthInt)),
            year,
            prescriptionsDispensed: r?.prescriptionsDispensed ?? "-",
            nearMissPercentage: r?.nearMissPercentage ?? "-",
            description: r?.description ?? "-",
            cause: r?.cause ?? "-",
            actionPlan: r?.actionPlan ?? "-",
            actionPlanReview: r?.actionPlanReview ?? "-",
            pharmacistName: pharmacistName || "-",
            otherStaffNames: Array.isArray(otherStaffNames) ? otherStaffNames : []
          };
        }).sort((a, b) => b.createdAtMs - a.createdAtMs);

        if (!cancelled) {
          setStaffMap(staffDict);
          setReports(norm);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || String(err));
          setLoading(false);
        }
      }
    }

    fetchAll();

    return () => {
      cancelled = true;
    };
  }, [pharmacyName]);

  // Fetch engine-generated monthly summaries (amber cards) in parallel
  useEffect(() => {
    if (!pharmacyName) return;
    let cancelled = false;

    async function fetchEngineSummaries() {
      setEngineLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) { setEngineLoading(false); return; }

        const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_monthly_summaries", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ pharmacyName, limit: 24 })
        });

        if (!res.ok) { if (!cancelled) setEngineLoading(false); return; }
        const json = await res.json();
        if (cancelled) return;
        setEngineSummaries(Array.isArray(json.summaries) ? json.summaries : []);
      } catch (_) {
        // non-fatal — the white cards still render
      } finally {
        if (!cancelled) setEngineLoading(false);
      }
    }

    fetchEngineSummaries();
    return () => { cancelled = true; };
  }, [pharmacyName]);

  // Build interleaved card list — engine summaries (amber) + user reports (white)
  // sorted by year+month descending. Both kinds may exist for the same month.
  const userCards = reports.map(r => ({
    kind: "user",
    year: r.year,
    month: r.month,
    sortKey: (r.year || 0) * 100 + (r.month || 0),
    data: r
  }));
  const engineCards = engineSummaries.map(s => ({
    kind: "engine",
    year: s.year,
    month: s.month,
    sortKey: (s.year || 0) * 100 + (s.month || 0),
    data: s
  }));
  const mergedCards = [...userCards, ...engineCards].sort((a, b) => b.sortKey - a.sortKey);

  return (
    <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee", background: "#f9fafb", fontWeight: 600 }}>
        Monthly Near Miss Review
      </div>

      {loading ? (
        <div style={{ padding: 12, fontSize: 14 }}>Loading monthly reports…</div>
      ) : error ? (
        <div style={{ padding: 12, color: "#b91c1c" }}>Error: {error}</div>
      ) : mergedCards.length === 0 ? (
        <div style={{ padding: 12, fontSize: 14 }}>
          {engineLoading ? "Loading…" : "No monthly reports or engine summaries found."}
        </div>
      ) : (
        <div style={{ padding: 10, overflowY: "auto" }}>
          {mergedCards.map((card, i) => {
            if (card.kind === "engine") {
              return (
                <MonthlySummaryCard
                  key={`engine-${card.year}-${card.month}`}
                  summary={card.data}
                />
              );
            }

            const r = card.data;
            return (
              <div key={`user-${i}`} style={{ border: "1px solid #e5e7eb", borderRadius: 8, padding: 10, marginBottom: 10 }}>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>
                  {fmtDdMmYyyyHhMm(r.createdAt)} • Month in review: {r.monthName} {r.year}
                </div>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div><strong>Items scanned:</strong> {r.prescriptionsDispensed}</div>
                  <div>
                    <strong>Near miss percentage:</strong>{" "}
                    {typeof r.nearMissPercentage === "number"
                      ? r.nearMissPercentage.toFixed(1)
                      : r.nearMissPercentage}
                  </div>
                </div>

                <div style={{ marginTop: 8 }}><strong>Description:</strong> {r.description}</div>
                <div style={{ marginTop: 4 }}><strong>Cause:</strong> {r.cause}</div>
                <div style={{ marginTop: 4 }}><strong>Action plan:</strong> {r.actionPlan}</div>
                <div style={{ marginTop: 4 }}><strong>Action plan review:</strong> {r.actionPlanReview}</div>

                <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div><strong>Pharmacist:</strong> {r.pharmacistName}</div>
                  <div>
                    <strong>Staff attending:</strong>{" "}
                    {r.otherStaffNames && r.otherStaffNames.length
                      ? r.otherStaffNames.join(", ")
                      : "—"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


///code for near miss level 3 4 analysis
/** ========== INSIGHTS PANEL: surfaces engine-computed insights for a branch ========== */
function InsightsPanel({ pharmacyName }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [insights, setInsights] = useState([]);
  const [meta, setMeta] = useState({});
  const [refreshing, setRefreshing] = useState(false);

  const tokenRef = useRef(null);
  useEffect(() => { tokenRef.current = localStorage.getItem("authToken"); }, []);

  useEffect(() => {
    if (!pharmacyName) return;
    let cancelled = false;

    async function fetchInsights() {
      setLoading(true);
      setError("");
      try {
        const token = tokenRef.current;
        if (!token) throw new Error("Not authenticated. Please log in.");

        const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_insights", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({ pharmacyName })
        });

        if (!res.ok) {
          const text = await res.text();
          throw new Error(text || `HTTP ${res.status}`);
        }

        const json = await res.json();
        if (cancelled) return;
        setInsights(Array.isArray(json.insights) ? json.insights : []);
        setMeta({
          computedAt: json.computedAt,
          windowStart: json.windowStart,
          windowEnd: json.windowEnd,
          estimated: json.estimated,
          populationRate: json.populationRate,
          ...(json.meta || {})
        });
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchInsights();
    return () => { cancelled = true; };
  }, [pharmacyName]);

  const handleRefresh = async () => {
    if (refreshing || !pharmacyName) return;
    setRefreshing(true);
    setError("");
    try {
      const token = tokenRef.current;
      if (!token) throw new Error("Not authenticated. Please log in.");

      const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_insights_refresh", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ pharmacyName })
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || `HTTP ${res.status}`);
      }

      const json = await res.json();
      setInsights(Array.isArray(json.insights) ? json.insights : []);
      setMeta({
        computedAt: json.computedAt,
        windowStart: json.windowStart,
        windowEnd: json.windowEnd,
        estimated: json.estimated,
        populationRate: json.populationRate,
        ...(json.meta || {})
      });
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setRefreshing(false);
    }
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '';

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{
        padding: "8px 10px",
        borderBottom: "1px solid #eee",
        background: "#f9fafb",
        fontWeight: 600,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8
      }}>
        <span>Insights{insights.length > 0 ? ` (${insights.length})` : ""}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {meta.computedAt && (
            <span style={{ fontSize: 11, fontWeight: 400, color: "#6b7280" }}>
              Computed {fmtDate(meta.computedAt)} • window {fmtDate(meta.windowStart)} → {fmtDate(meta.windowEnd)}
              {meta.estimated ? " • rates estimated" : ""}
            </span>
          )}
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            title="Recompute insights for this branch now"
            style={{
              fontSize: 11,
              padding: "3px 8px",
              border: "1px solid #9ca3af",
              borderRadius: 4,
              background: refreshing ? "#f3f4f6" : "#fff",
              color: refreshing ? "#9ca3af" : "#374151",
              cursor: refreshing ? "wait" : "pointer",
              fontWeight: 500
            }}
          >
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div style={{ flex: 1, overflow: "auto", padding: 8 }}>
        {loading && (
          <div style={{ color: "#6b7280", fontSize: 12 }}>Loading insights…</div>
        )}
        {error && (
          <div style={{ color: "#dc2626", fontSize: 12 }}>Error: {error}</div>
        )}
        {!loading && !error && insights.length === 0 && (
          <div style={{ color: "#6b7280", fontSize: 13, padding: "8px 4px", lineHeight: 1.5 }}>
            No patterns to flag for this period. Either the data is too thin to surface
            a signal, or the pattern is stable. The monthly review remains valuable for
            capturing context the engine can't see.
          </div>
        )}
        {!loading && !error && insights.length > 0 && (
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
            {insights.map((ins, i) => (
              <InsightRow key={`${ins.id}-${i}`} insight={ins} />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** ========== INSIGHT ROW: single insight card with action draft button ========== */
function InsightRow({ insight }) {
  const [showDraft, setShowDraft] = useState(false);

  const colorMap = {
    "I-1": "#fbbf24", "I-2": "#60a5fa", "I-3": "#a78bfa",
    "I-4": "#f87171", "I-5": "#34d399", "I-6": "#9ca3af",
    "I-7": "#ef4444", "I-8": "#9ca3af", "I-9": "#fb923c"
  };
  const color = colorMap[insight.id] || "#6b7280";
  const hasDraft = !!(insight.action && insight.action.draft);

  return (
    <li style={{
      padding: "8px 10px", marginBottom: 6,
      border: "1px solid #e5e7eb", borderLeft: `4px solid ${color}`,
      borderRadius: 4, background: "#fff"
    }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>{insight.title}</span>
        <span style={{ fontSize: 10, color: "#9ca3af" }}>{insight.id}</span>
      </div>
      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5 }}>
        {insight.detail}
      </div>
      {insight.action && insight.action.label && (
        <div style={{ marginTop: 6 }}>
          <button
            onClick={() => hasDraft && setShowDraft(true)}
            disabled={!hasDraft}
            title={hasDraft ? "Open the draft" : "No draft available for this action type"}
            style={{
              fontSize: 11,
              padding: "4px 10px",
              border: `1px solid ${hasDraft ? "#9ca3af" : "#d1d5db"}`,
              borderRadius: 4,
              background: hasDraft ? "#fff" : "#f9fafb",
              color: hasDraft ? "#374151" : "#9ca3af",
              cursor: hasDraft ? "pointer" : "not-allowed"
            }}
          >
            {insight.action.label}
          </button>
        </div>
      )}

      {showDraft && (
        <DraftModal
          title={insight.action.label}
          insightTitle={insight.title}
          draft={insight.action.draft}
          onClose={() => setShowDraft(false)}
        />
      )}
    </li>
  );
}

/** ========== DRAFT MODAL: shows the action draft text with copy-to-clipboard ========== */
function DraftModal({ title, insightTitle, draft, onClose }) {
  const [copied, setCopied] = useState(false);

  if (!draft) return null;

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(draft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {}
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center",
        zIndex: 1000
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(680px, 92vw)",
          maxHeight: "85vh",
          background: "#fff",
          borderRadius: 8,
          display: "flex", flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
        }}
      >
        <div style={{
          padding: "12px 16px",
          borderBottom: "1px solid #eee",
          display: "flex", justifyContent: "space-between", alignItems: "baseline"
        }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{title}</div>
            <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>For: {insightTitle}</div>
          </div>
          <button
            onClick={onClose}
            style={{
              border: "none", background: "transparent",
              fontSize: 20, cursor: "pointer", color: "#6b7280"
            }}
          >×</button>
        </div>

        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          <pre style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontFamily: "inherit",
            fontSize: 13,
            lineHeight: 1.6,
            color: "#1f2937",
            margin: 0
          }}>
            {draft}
          </pre>
        </div>

        <div style={{
          padding: "10px 16px",
          borderTop: "1px solid #eee",
          display: "flex", justifyContent: "space-between", alignItems: "center"
        }}>
          <span style={{ fontSize: 11, color: "#6b7280", fontStyle: "italic" }}>
            This is a draft. Review and edit before use.
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={copyToClipboard}
              style={{
                fontSize: 12,
                padding: "6px 14px",
                border: "1px solid #9ca3af",
                borderRadius: 4,
                background: "#fff",
                cursor: "pointer"
              }}
            >
              {copied ? "Copied ✓" : "Copy to clipboard"}
            </button>
            <button
              onClick={onClose}
              style={{
                fontSize: 12,
                padding: "6px 14px",
                border: "1px solid #9ca3af",
                borderRadius: 4,
                background: "#f3f4f6",
                cursor: "pointer"
              }}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

//


/** ========== NEAR MISS SPLIT: wrapper combining left (current) + right (monthly) ========== */
// NearMissSplitDialog
 

function NearMissSplitDialog({ popupData }) {
  const { pharmacyName, pharmacyId } = popupData;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12, height: "70vh", minHeight: 420 }}>
      {/* Insights panel — full-width across the top */}
      <div style={{
        border: "1px solid #e5e7eb",
        borderRadius: 8,
        overflow: "hidden",
        flex: "0 0 35%",
        minHeight: 140
      }}>
        <InsightsPanel pharmacyName={pharmacyName} />
      </div>

      {/* Existing two-column layout — categories left, monthly reviews right */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 12,
        flex: 1,
        minHeight: 0
      }}>
        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid #eee", background: "#f9fafb", fontWeight: 600 }}>
            Near Miss by Category
          </div>

          <div style={{ flex: 1, overflow: "auto", padding: 8 }}>
            <PopupContent popupData={{ ...popupData, column: "Near Miss" }} />
          </div>
        </div>

        <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
          <NearMissMonthlyPanel pharmacyName={pharmacyName} pharmacyId={pharmacyId} />
        </div>
      </div>
    </div>
  );
}


/** Self-contained PopupContent with column picker + flattening for dynamic datasets */
function PopupGrid({ popupData }) {
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSizeState, setPageSize] = useState(15);
  const [pickerOpen, setPickerOpen] = useState(false);

  // --- helpers to flatten & normalise values so ALL fields display ---
  const normaliseVal = (v) => {
    if (v == null) return null;
    if (v instanceof Date) return v.toISOString();

    if (typeof v === "object") {
      if (v.$oid && typeof v.$oid === "string") return v.$oid;
      if (v.$date && typeof v.$date === "string") return v.$date;
    }

    return v;
  };

  const flattenRow = (row, prefix = "", out = {}, depth = 0) => {
    if (row == null) return out;

    if (depth > 3) {
      out[prefix.slice(0, -1)] = JSON.stringify(row);
      return out;
    }

    if (Array.isArray(row)) {
      out[prefix ? prefix.slice(0, -1) : "[]"] = row.map(x => typeof x === "object" ? JSON.stringify(x) : String(x)).join(" | ");

      row.slice(0, 3).forEach((v, i) => {
        if (typeof v === "object") out[`${prefix}${i}`] = JSON.stringify(v);
        else out[`${prefix}${i}`] = normaliseVal(v);
      });

      return out;
    }

    if (typeof row !== "object") {
      out[prefix ? prefix.slice(0, -1) : "value"] = normaliseVal(row);
      return out;
    }

    for (const k of Object.keys(row)) {
      const v = row[k];

      if (v && typeof v === "object" && !Array.isArray(v)) {
        flattenRow(v, `${prefix}${k}.`, out, depth + 1);
      } else {
        out[`${prefix}${k}`] = normaliseVal(v);
      }
    }

    return out;
  };

  // Normalise incoming shapes
  const rawRows = useMemo(() => {
    const p = popupData;
    if (!p) return [];
    if (Array.isArray(p)) return p;
    if (Array.isArray(p.answer)) return p.answer;
    if (Array.isArray(p.rows)) return p.rows;
    if (Array.isArray(p.data)) return p.data;
    if (Array.isArray(p.results)) return p.results;

    // Known API keys
    if (Array.isArray(p.fridgeLogs)) return p.fridgeLogs;
    if (Array.isArray(p.shelfcheckdetails)) return p.shelfcheckdetails;
    if (Array.isArray(p.shelfDetails)) return p.shelfDetails;
    if (Array.isArray(p.cleaningDetails)) return p.cleaningDetails;
    if (Array.isArray(p.handoverDetails)) return p.handoverDetails;
    if (Array.isArray(p.nearMissDetails)) return p.nearMissDetails;
    if (Array.isArray(p.stockDetails)) return p.stockDetails;

    // Special case: deliveryDetails.points is [lat,long] arrays
    if (p?.deliveryDetails?.points && Array.isArray(p.deliveryDetails.points)) {
      return p.deliveryDetails.points.map(([lat, long]) => ({ lat, long }));
    }

    return [];
  }, [popupData]);

  // Flatten rows so nested fields become columns
  const flatRows = useMemo(() => rawRows.map(r => flattenRow(r)), [rawRows]);

  // Columns = union of keys across a sample
  const autoColumns = useMemo(() => {
    const keys = new Set();
    flatRows.slice(0, 200).forEach(r => Object.keys(r || {}).forEach(k => keys.add(k)));
    return Array.from(keys);
  }, [flatRows]);

  const [visibleCols, setVisibleCols] = useState(autoColumns);

  useEffect(() => {
    setVisibleCols(autoColumns);
  }, [autoColumns]);

  // Search
  const filteredRows = useMemo(() => {
    const q = (searchTerm || "").trim().toLowerCase();

    if (!q) return flatRows;

    const cols = (visibleCols.length ? visibleCols : autoColumns);

    return flatRows.filter(row =>
      cols.some(col => {
        const v = row?.[col];
        let s = "";

        if (typeof v === "string") s = v;
        else if (v instanceof Date) s = v.toISOString();
        else if (v == null) s = "";
        else if (typeof v === "object") s = JSON.stringify(v);
        else s = String(v);

        return s.toLowerCase().includes(q);
      })
    );
  }, [flatRows, searchTerm, visibleCols, autoColumns]);

  // Paging
  const total = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSizeState));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * pageSizeState;
  const pageRows = filteredRows.slice(start, start + pageSizeState);
  const colsToRender = visibleCols.length ? visibleCols : autoColumns;

  const toggleCol = (c) =>
    setVisibleCols(prev => prev.includes(c) ? prev.filter(x => x !== c) : [...prev, c]);

  const selectAll = () => setVisibleCols(autoColumns);
  const selectNone = () => setVisibleCols([]);

  const formatCell = (v) => {
    if (v == null) return "—";
    if (v instanceof Date) return v.toLocaleString();
    if (typeof v === "object") return JSON.stringify(v);
    return String(v);
  };

  // CSV export of visible columns
  const exportCSV = () => {
    const rows = filteredRows;
    const cols = colsToRender;

    const esc = (s) => {
      const str = s == null ? "" : String(s);
      if (/[",\n]/.test(str)) return '"' + str.replace(/"/g, '""') + '"';
      return str;
    };

    const header = cols.join(",");
    const body = rows.map(r => cols.map(c => {
      const v = r?.[c];
      return esc(v instanceof Date ? v.toISOString() : (typeof v === "object" ? JSON.stringify(v) : v));
    }).join(",")).join("\n");

    const csv = header + "\n" + body;
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = "ai-data.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };

  // Expose export so existing header button can call it
  useEffect(() => {
    window.__AI_EXPORT__ = exportCSV;

    return () => {
      if (window.__AI_EXPORT__ === exportCSV) delete window.__AI_EXPORT__;
    };
  }, [filteredRows, colsToRender]);

  return (
    <div className="ai-grid">
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
        <button className="btn btn-light" onClick={() => setPickerOpen(o => !o)}>Columns</button>
        <button className="btn btn-primary" onClick={exportCSV}>Export to Excel</button>

        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8 }}>
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search..."
            value={searchTerm}
            onChange={e => {
              setPage(1);
              setSearchTerm(e.target.value);
            }}
            style={{ width: 220 }}
          />

          <select
            className="form-select form-select-sm"
            value={pageSizeState}
            onChange={e => {
              setPage(1);
              setPageSize(Number(e.target.value));
            }}
            style={{ width: 80 }}
          >
            <option value={10}>10</option>
            <option value={15}>15</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      <div style={{ padding: 8, border: "1px solid #ddd", borderRadius: 6, marginBottom: 8, maxHeight: 220, overflowY: "auto", display: pickerOpen ? "block" : "none" }}>
        <div style={{ marginBottom: 6 }}>
          <button className="btn btn-sm btn-light" onClick={selectAll}>Select all</button>{" "}
          <button className="btn btn-sm btn-light" onClick={selectNone}>Select none</button>
        </div>

        {autoColumns.length === 0 ? (
          <div>No columns</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 6 }}>
            {autoColumns.map(c => (
              <label key={c} style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="checkbox" checked={visibleCols.includes(c)} onChange={() => toggleCol(c)} />
                <span>{c}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="table-wrapper" style={{ maxHeight: 420, overflow: "auto", border: "1px solid #eee", borderRadius: 6 }}>
        <table className="table table-sm" style={{ width: "100%", tableLayout: "auto" }}>
          <thead>
            <tr>
              {((visibleCols.length ? visibleCols : ["_raw"])).map(c => (
                <th key={c} style={{ whiteSpace: "nowrap", position: "sticky", top: 0, background: "#f8f9fa" }}>{c}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {(visibleCols.length === 0) ? (
              <tr>
                <td colSpan={1} style={{ textAlign: "left", padding: 8 }}>
                  <pre style={{ margin: 0, maxHeight: 360, overflow: "auto" }}>
                    {JSON.stringify(rawRows.slice(start, start + pageSizeState), null, 2)}
                  </pre>
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td colSpan={visibleCols.length} style={{ textAlign: "center", padding: 16 }}>No matching items.</td>
              </tr>
            ) : pageRows.map((row, i) => (
              <tr key={i} style={{ backgroundColor: i % 2 === 0 ? "#ffffff" : "#f5f5f5" }}>
                {(visibleCols.length ? visibleCols : autoColumns).map(c => (
                  <td key={c}>{formatCell(row?.[c])}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 8 }}>
        <button className="btn btn-sm btn-light" disabled={currentPage <= 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</button>
        <span style={{ fontSize: 12 }}>Page {currentPage} of {totalPages}</span>
        <button className="btn btn-sm btn-light" disabled={currentPage >= totalPages} onClick={() => setPage(p => Math.min(totalPages, p + 1))}>Next</button>
      </div>
    </div>
  );
}

/* ===================== DASHBOARD ===================== */

/* UPDATED labels: added "SIM- backend send" and kept split SIM columns */
const columnLabels = [
  "Scanned", "Collections", "Del-items","Del-pats", "Walk-ins",
  "Bags on shelves", "% msg sent","Oldest bag", "Bags under 1mth", "Bags over 1mth", "Total stock £", "Exp/Slow value £","Shelf Check",
  "Fridge", "Cleaning", "Cashup", "RP Reg", "Handover", "MRHA",
  "Items/mth", "NMS Comp", "NMS Wait", "Interv", "Near Miss in 30 days", "Incidents", "Complaints","Accident book",
  "SMS Sent", "Email Sent", "Incoming", "SMS Signup", "Email Signup",
  "Use SIM msg", "Notify service", "No. SIM", "Sim-waiting", "Sim-del", "SIM- backend send", "Sim-failures", "Bulk waiting", "Bulk sent",  "Appointments",
  "Near Miss Count",
  "Tasks",
  "NMS Count",
  "Temperature",
  "Cleaning Count",
  "CD Count"
];

/* UPDATED keys to match backend: waitingCount, deliveredCount, backendSentCount, otherCount */
const columnKeyMap = [
  "scanned", "collections", "deliveries","drops", "walkIns",
  "bagsOnShelves","itemsWithMsg", "oldestBag", "bagsUnder1Month", "bagsOver1Month", "totalValue", "expSlowValue",
  "shelfCheck","fridge", "cleaning", "cashup", "rpReg", "handover", "MRHA",
  "itemsPerMonth", "nmsComp", "nmsWait", "interventions", "nearMisses", "incidents", "complaints","accidents",
  "smsSent", "emailSent", "incoming", "smsSignup", "emailSignup",
  "useSimCardMessaging", "notificationService","sim_number", "waitingCount", "deliveredCount", "backendSentCount", "otherCount", "bulkWaiting", "bulkSent", "Apptcount",
  "Nearmisscount",
  "Taskcount",
  "NMScount",
  "Tempcount",
  "Cleancount",
  "CDcount"
];

/** ========== NEAR MISS ANALYSIS: fleet-wide grid view ========== */

const HEALTH_COLOURS = {
  healthy:   { bg: "#dcfce7", text: "#14532d", border: "#86efac" },
  watch:     { bg: "#fef3c7", text: "#78350f", border: "#fbbf24" },
  concern:   { bg: "#fee2e2", text: "#7f1d1d", border: "#fca5a5" },
  "non-user": { bg: "#f3f4f6", text: "#374151", border: "#d1d5db" }
};

// Palette used to outline each organisation's group in the analysis grid.
// Cycled by index so adjacent groups read as distinct cards. Each entry has:
//   rim:  the 3px outline colour
//   band: the (light, tinted) background of the group header row
//   text: header text colour, dark enough to read against `band`
const GROUP_RIM_PALETTE = [
  { rim: "#6366f1", band: "#eef2ff", text: "#3730a3" }, // indigo
  { rim: "#0891b2", band: "#ecfeff", text: "#155e75" }, // cyan
  { rim: "#16a34a", band: "#f0fdf4", text: "#14532d" }, // green
  { rim: "#ea580c", band: "#fff7ed", text: "#7c2d12" }, // orange
  { rim: "#9333ea", band: "#faf5ff", text: "#581c87" }  // purple
];

function HealthCell({ status, reason, children, style }) {
  const c = HEALTH_COLOURS[status] || HEALTH_COLOURS["non-user"];
  return (
    <td
      style={{
        background: c.bg,
        color: c.text,
        padding: "8px 10px",
        borderRight: "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        verticalAlign: "top",
        fontSize: 12,
        lineHeight: 1.4,
        ...style
      }}
      title={reason || ""}
    >
      {children}
    </td>
  );
}

function PlainCell({ children, align = "left", title, style }) {
  return (
    <td
      style={{
        padding: "8px 10px",
        borderRight: "1px solid #e5e7eb",
        borderBottom: "1px solid #e5e7eb",
        verticalAlign: "top",
        fontSize: 12,
        lineHeight: 1.4,
        textAlign: align,
        ...style
      }}
      title={title || ""}
    >
      {children}
    </td>
  );
}

function StatusBadge({ status }) {
  const c = HEALTH_COLOURS[status] || HEALTH_COLOURS["non-user"];
  const label = status === "non-user" ? "Non-user" : status[0].toUpperCase() + status.slice(1);
  return (
    <span style={{
      display: "inline-block",
      padding: "2px 8px",
      borderRadius: 12,
      fontSize: 10,
      fontWeight: 600,
      background: c.border,
      color: c.text,
      textTransform: "uppercase",
      letterSpacing: 0.3
    }}>
      {label}
    </span>
  );
}

function fmtPctOrDash(n) {
  if (n === null || n === undefined) return "—";
  return `${Math.round(n * 100)}%`;
}

function fmtRateGridCell(rate, basis) {
  if (rate === null || rate === undefined) return "—";
  if (basis === "items") return `${(rate * 100).toFixed(2)}%`;
  if (basis === "dispenser-count") return `${rate.toFixed(2)}/disp`;
  return Number(rate).toFixed(4);
}

function fmtDateShort(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${String(dt.getFullYear()).slice(-2)}`;
}

function NearMissAnalysis() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [actionTarget, setActionTarget] = useState(null);
  // Sort state — { key, direction }. When key is null, the table falls back
  // to the default org-grouped view. Click a sortable column to set; click
  // again to flip direction; third click clears the sort.
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  // Hide branches that aren't using the tool ("⚠ Inactive") — they're just
  // noise in the fleet grid. Default on; toggle in the header to reveal them.
  const [hideInactive, setHideInactive] = useState(true);

  // Trend dropdown selection — null means "Overall" (fleet for admin, the
  // viewer's org for non-admin). Otherwise the pharmacyId of one specific
  // branch from the current filtered list.
  const [selectedBranchId, setSelectedBranchId] = useState(null);

  // Bar-click target for the per-month histogram modal. Null means closed.
  // Shape: { year, month, monthLabel } passed up from TrendGraph.
  const [monthBreakdownTarget, setMonthBreakdownTarget] = useState(null);

  // Near-miss detail popup (same dialog as the mockup grid's Near Miss column).
  // Null means closed; otherwise the popupData shape NearMissSplitDialog expects.
  const [nmPopupData, setNmPopupData] = useState(null);
  const [nmPopupLoading, setNmPopupLoading] = useState(false);

  // Open the near-miss split dialog for a clicked branch. Mirrors the
  // handleCellClick "Near Miss in 30 days" branch in the mockup table.
  const openNearMissPopup = async (branch) => {
    setNmPopupLoading(true);
    try {
      const token = tokenRef.current || localStorage.getItem("authToken");
      const response = await fetch("https://api.pharmacegroup.co.uk/nearmissdetails", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ pharmacyName: branch.pharmacyName })
      });
      if (!response.ok) throw new Error("Failed to fetch near miss data");
      const result = await response.json();
      setNmPopupData({
        column: "Near Miss in 30 days",
        pharmacyName: branch.pharmacyName,
        pharmacyId: branch.pharmacyId,
        dbName: branch.pharmacyId,
        nearMissDetails: result.details,
        isNearMissPopup: true
      });
    } catch (err) {
      console.error("Error fetching near miss data:", err);
    } finally {
      setNmPopupLoading(false);
    }
  };

  // Viewer's organisation — drives whether they see the full fleet or only
  // their own branches. "Pharmace all" is the master/admin org; everyone else
  // is filtered to branches whose organisationName matches theirs.
  const viewerOrg = localStorage.getItem("organisationName") || "";
  const isAdmin = viewerOrg === "Pharmace all";

  // Pharmacy-name masking for admin demos. Display-only; sort keys, group
  // labels, API bodies and email content all stay unmasked.
  const masked = useMaskPharmacyNames();

  const tokenRef = useRef(null);
  useEffect(() => { tokenRef.current = localStorage.getItem("authToken"); }, []);

  const loadGrid = async () => {
    setLoading(true);
    setError("");
    try {
      const token = tokenRef.current;
      if (!token) throw new Error("Not authenticated. Please log in.");

      const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_analysis_grid", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({})
      });
      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadGrid(); }, []);

  if (loading) {
    return <div style={{ padding: 24, color: "#6b7280" }}>Loading fleet analysis…</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: "#dc2626" }}>Error: {error}</div>;
  }
  if (!data || !data.branches || data.branches.length === 0) {
    return (
      <div style={{ padding: 24, color: "#6b7280" }}>
        No fleet analysis data available yet. The engine computes this on the nightly run —
        if you've just deployed, trigger a manual run.
      </div>
    );
  }

  // Sort vs default org-grouping. When sortConfig.key is set, dissolve the
  // org sections and render one flat sorted list. Otherwise group by org.
  // Non-admins only see branches in their own organisation; "Pharmace all"
  // (isAdmin) sees the full fleet including Unaffiliated branches.
  const orgScopedBranches = isAdmin
    ? data.branches
    : data.branches.filter(b => (b.organisationName || "") === viewerOrg);

  // Count how many are inactive (not using the tool) so the toggle can show it.
  const inactiveCount = orgScopedBranches.filter(b => !b.isUsingTool).length;

  // Hide inactive branches when the toggle is on — they're just noise.
  const branches = hideInactive
    ? orgScopedBranches.filter(b => b.isUsingTool)
    : orgScopedBranches;

  // Filtered-empty state (non-admin whose org has no branches in the response)
  if (orgScopedBranches.length === 0) {
    return (
      <div style={{ padding: 24, color: "#6b7280" }}>
        No branches found{viewerOrg ? ` for ${viewerOrg}` : ""} in this month's fleet
        analysis. If you've recently been added to an organisation, the engine will
        pick you up on the next nightly run.
      </div>
    );
  }

  // Comparator — nulls/undefined always sink to the bottom regardless of
  // direction (so "best at the top" feels right whether asc or desc).
  const compareBranches = (a, b, key, direction) => {
    const va = a[key];
    const vb = b[key];
    const aNull = va === null || va === undefined;
    const bNull = vb === null || vb === undefined;
    if (aNull && bNull) return 0;
    if (aNull) return 1;
    if (bNull) return -1;
    if (typeof va === "string" && typeof vb === "string") {
      return direction === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    return direction === "asc" ? va - vb : vb - va;
  };

  const handleSort = (key) => {
    setSortConfig(prev => {
      if (prev.key !== key) return { key, direction: "asc" };
      if (prev.direction === "asc") return { key, direction: "desc" };
      // Third click clears the sort, returning to org-grouped view.
      return { key: null, direction: "asc" };
    });
  };

  const sortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

  let sortedBranches = null;
  let orgGroups = [];
  if (sortConfig.key) {
    sortedBranches = [...branches].sort((a, b) =>
      compareBranches(a, b, sortConfig.key, sortConfig.direction));
  } else {
    const byOrg = {};
    for (const b of branches) {
      const k = b.organisationName || "Unaffiliated";
      if (!byOrg[k]) byOrg[k] = [];
      byOrg[k].push(b);
    }
    orgGroups = Object.entries(byOrg).sort((a, b) => {
      if (a[0] === "Unaffiliated") return 1;
      if (b[0] === "Unaffiliated") return -1;
      return a[0].localeCompare(b[0]);
    });
  }

  // Top-line counts
  const healthCounts = { healthy: 0, watch: 0, concern: 0, "non-user": 0 };
  for (const b of branches) {
    healthCounts[b.healthStatus] = (healthCounts[b.healthStatus] || 0) + 1;
  }

  // Header cell helper — pass sortKey for clickable/sortable, omit for static.
  const SortableTh = ({ sortKey, children }) => {
    const isSortable = !!sortKey;
    const isActive = sortConfig.key === sortKey;
    return (
      <th
        onClick={isSortable ? () => handleSort(sortKey) : undefined}
        style={{
          ...thStyle,
          cursor: isSortable ? "pointer" : "default",
          userSelect: "none",
          background: isActive ? "#eef2ff" : undefined,
          whiteSpace: "nowrap"
        }}
        title={isSortable ? "Click to sort (3rd click clears)" : undefined}
      >
        {children}{isSortable && sortIndicator(sortKey)}
      </th>
    );
  };

  return (
    <div style={{ padding: "16px 20px" }}>
      {/* Header strip */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20 }}>Near Miss Analysis</h2>
          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
            {isAdmin ? "Fleet view" : `${viewerOrg} view`} for {data.month?.monthLabel || "—"} · {branches.length} branch{branches.length === 1 ? "" : "es"}
            {sortConfig.key && (
              <span style={{ marginLeft: 10, color: "#4338ca" }}>
                · sorted by {sortConfig.key} ({sortConfig.direction})
                <button
                  onClick={() => setSortConfig({ key: null, direction: "asc" })}
                  style={{
                    marginLeft: 8, fontSize: 11, padding: "2px 8px",
                    border: "1px solid #c7d2fe", borderRadius: 4,
                    background: "#fff", color: "#4338ca", cursor: "pointer"
                  }}
                >
                  Clear sort
                </button>
              </span>
            )}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <MaskToggleButton />
          {Object.entries(healthCounts).filter(([, n]) => n > 0).map(([status, n]) => (
            <span key={status}>
              <StatusBadge status={status} /> <span style={{ fontSize: 12, color: "#374151" }}>{n}</span>
            </span>
          ))}
          {inactiveCount > 0 && (
            <button
              onClick={() => setHideInactive(v => !v)}
              title={hideInactive
                ? `${inactiveCount} inactive branch${inactiveCount === 1 ? "" : "es"} hidden — click to show`
                : `Showing ${inactiveCount} inactive branch${inactiveCount === 1 ? "" : "es"} — click to hide`}
              style={{
                fontSize: 12, padding: "6px 12px",
                border: "1px solid #9ca3af", borderRadius: 4,
                background: hideInactive ? "#fff" : "#eef2ff",
                color: "#374151", cursor: "pointer", marginLeft: 8
              }}
            >
              {hideInactive
                ? `Show inactive (${inactiveCount})`
                : `Hide inactive (${inactiveCount})`}
            </button>
          )}
          <button
            onClick={loadGrid}
            style={{
              fontSize: 12, padding: "6px 12px",
              border: "1px solid #9ca3af", borderRadius: 4,
              background: "#fff", cursor: "pointer", marginLeft: 8
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Trend graph — placed above the grid; has a "View" dropdown so the
          user can switch between overall (org / fleet) and an individual
          branch. The chart re-fetches when the selection changes. */}
      <div style={{ marginBottom: 16, padding: 16, border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 14 }}>
            Trend — last 12 months ({
              selectedBranchId
                ? (displayName(branches.find(b => String(b.pharmacyId) === selectedBranchId)?.pharmacyName, masked) || "branch")
                : (isAdmin ? "all sites" : viewerOrg)
            })
          </h3>
          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: "#374151" }}>
            View:
            <select
              value={selectedBranchId || ""}
              onChange={e => setSelectedBranchId(e.target.value || null)}
              style={{
                fontSize: 12,
                padding: "4px 8px",
                border: "1px solid #9ca3af",
                borderRadius: 4,
                background: "#fff",
                cursor: "pointer",
                minWidth: 200
              }}
            >
              <option value="">
                {isAdmin ? "Overall (all sites)" : `Overall (${viewerOrg})`}
              </option>
              {[...branches]
                .sort((a, b) => (a.pharmacyName || "").localeCompare(b.pharmacyName || ""))
                .map(b => (
                  <option key={String(b.pharmacyId)} value={String(b.pharmacyId)}>
                    {displayName(b.pharmacyName, masked)}
                  </option>
                ))
              }
            </select>
          </label>
        </div>
        <TrendGraph
          organisationName={isAdmin ? null : viewerOrg}
          pharmacyId={selectedBranchId}
          onBarClick={target => setMonthBreakdownTarget(target)}
        />
      </div>

      {/* Grid */}
      <div style={{ overflowX: "auto", border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
          <thead style={{ position: "sticky", top: 0, background: "#f9fafb", zIndex: 1 }}>
            <tr>
              <SortableTh sortKey="pharmacyName">Branch</SortableTh>
              <SortableTh sortKey="healthStatus">Summary</SortableTh>
              <SortableTh sortKey="incidentsThisMonth">Tool usage</SortableTh>
              <SortableTh sortKey="monthlyReportCompletionRate">Monthly reviews</SortableTh>
              <SortableTh sortKey="rate">Rate</SortableTh>
              <SortableTh sortKey="insightsCount">Patterns</SortableTh>
              <SortableTh sortKey="alertsCount">Alerts</SortableTh>
              <SortableTh sortKey="pmiCount">PMI</SortableTh>
              <SortableTh sortKey="causeCompletionRate">Maturity</SortableTh>
              <SortableTh>Actions</SortableTh>
            </tr>
          </thead>
          <tbody>
            {sortedBranches ? (
              sortedBranches.map(b => (
                <BranchRow key={String(b.pharmacyId)} branch={b} onAction={() => setActionTarget(b)} onBranchClick={() => openNearMissPopup(b)} />
              ))
            ) : (
              orgGroups.map(([orgName, branchesInOrg], orgIdx) => {
                const palette = GROUP_RIM_PALETTE[orgIdx % GROUP_RIM_PALETTE.length];
                const branchCount = branchesInOrg.length;
                return (
                  <React.Fragment key={orgName}>
                    {orgIdx > 0 && (
                      <tr aria-hidden="true">
                        <td colSpan={10} style={{
                          height: 10,
                          background: "transparent",
                          border: "none",
                          padding: 0
                        }} />
                      </tr>
                    )}
                    <tr>
                      <td colSpan={10} style={{
                        background: palette.band,
                        padding: "8px 12px",
                        fontWeight: 700,
                        fontSize: 11,
                        textTransform: "uppercase",
                        letterSpacing: 0.5,
                        color: palette.text,
                        borderTop: `3px solid ${palette.rim}`,
                        borderLeft: `3px solid ${palette.rim}`,
                        borderRight: `3px solid ${palette.rim}`
                      }}>
                        {displayName(orgName, masked)} · {branchCount} branch{branchCount === 1 ? "" : "es"}
                      </td>
                    </tr>
                    {branchesInOrg.map((b, i) => {
                      const position = branchCount === 1
                        ? "only"
                        : i === 0
                          ? "first"
                          : i === branchCount - 1
                            ? "last"
                            : "middle";
                      return (
                        <BranchRow
                          key={String(b.pharmacyId)}
                          branch={b}
                          onAction={() => setActionTarget(b)}
                          onBranchClick={() => openNearMissPopup(b)}
                          groupColor={palette.rim}
                          position={position}
                        />
                      );
                    })}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {branches.length === 0 && hideInactive && inactiveCount > 0 && (
        <div style={{ padding: 16, color: "#6b7280", fontSize: 13, textAlign: "center" }}>
          All {inactiveCount} branch{inactiveCount === 1 ? " is" : "es are"} currently inactive and hidden.{" "}
          <button
            onClick={() => setHideInactive(false)}
            style={{
              fontSize: 12, padding: "2px 8px", border: "1px solid #c7d2fe",
              borderRadius: 4, background: "#fff", color: "#4338ca", cursor: "pointer"
            }}
          >
            Show inactive
          </button>
        </div>
      )}

      {actionTarget && (
        <ActionsModal
          branch={actionTarget}
          onClose={() => setActionTarget(null)}
        />
      )}
      {monthBreakdownTarget && (
        <MonthBreakdownModal
          target={monthBreakdownTarget}
          organisationName={isAdmin ? null : viewerOrg}
          pharmacyId={selectedBranchId}
          onClose={() => setMonthBreakdownTarget(null)}
        />
      )}

      {nmPopupLoading && (
        <div className="popup">
          <div className="popup-content">
            <div style={{ padding: 24, color: "#6b7280" }}>Loading near miss details…</div>
          </div>
        </div>
      )}

      {nmPopupData && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setNmPopupData(null)}>X</button>
            <NearMissSplitDialog popupData={nmPopupData} />
          </div>
        </div>
      )}
    </div>
  );
}

const thStyle = {
  textAlign: "left",
  padding: "10px 10px",
  fontSize: 11,
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "#374151",
  borderRight: "1px solid #e5e7eb",
  borderBottom: "1px solid #d1d5db"
};

function BranchRow({ branch, onAction, onBranchClick, groupColor, position }) {
  const masked = useMaskPharmacyNames();
  // Show the full narrative — let the cell wrap naturally. Title attribute
  // on the parent <HealthCell> still carries statusReason for hover context.
  const summaryText = branch.healthStatus === "non-user"
    ? "Not currently using the tool"
    : (branch.narrative || "—");

  // Group-rim border overrides. When groupColor is set, the row's first cell
  // gets a left rule, the last cell gets a right rule, and (if this is the
  // last/only row in the group) every cell gets a bottom rule — collectively
  // closing the box around the group.
  const RIM = "3px solid";
  const isLast = position === "last" || position === "only";
  const leftEdge = groupColor ? { borderLeft: `${RIM} ${groupColor}` } : null;
  const rightEdge = groupColor ? { borderRight: `${RIM} ${groupColor}` } : null;
  const bottomEdge = (groupColor && isLast) ? { borderBottom: `${RIM} ${groupColor}` } : null;

  const firstCellStyle = { ...(leftEdge || {}), ...(bottomEdge || {}) };
  const middleCellStyle = bottomEdge || undefined;
  const lastCellStyle = { ...(rightEdge || {}), ...(bottomEdge || {}) };

  return (
    <tr>
      <PlainCell style={firstCellStyle}>
        <div style={{ fontWeight: 600, color: "#1f2937" }}>{displayName(branch.pharmacyName, masked)}</div>
        <StatusBadge status={branch.healthStatus} />
      </PlainCell>

      <HealthCell status={branch.healthStatus} reason={branch.statusReason} style={middleCellStyle}>
        {summaryText}
      </HealthCell>

      <PlainCell style={middleCellStyle}>
        {branch.isUsingTool
          ? <><span
                onClick={onBranchClick}
                style={{
                  color: onBranchClick ? "#4338ca" : "inherit",
                  cursor: onBranchClick ? "pointer" : "default",
                  textDecoration: onBranchClick ? "underline" : "none",
                  fontWeight: onBranchClick ? 600 : "inherit"
                }}
                title={onBranchClick ? "View near miss details" : undefined}
              >
                {branch.incidentsThisMonth} this month
              </span><br />
              <span style={{ color: "#6b7280", fontSize: 10 }}>Last: {fmtDateShort(branch.lastIncidentDate)}</span></>
          : <span style={{ color: "#dc2626", fontWeight: 600 }}>⚠ Inactive</span>
        }
      </PlainCell>

      <PlainCell
        style={middleCellStyle}
        title={branch.monthlyReportLastDate
          ? `Last review on ${fmtDateShort(branch.monthlyReportLastDate)}`
          : "No monthly reviews recorded"
        }
      >
        <div style={{
          color: branch.monthlyReportCompletionRate < 0.5 ? "#dc2626" : "#1f2937",
          fontWeight: 600
        }}>
          {fmtPctOrDash(branch.monthlyReportCompletionRate)}
        </div>
        <div style={{ color: "#6b7280", fontSize: 10 }}>
          {branch.monthlyReportCount12mo}/12 mo · last {fmtDateShort(branch.monthlyReportLastDate)}
        </div>
      </PlainCell>

      <PlainCell style={middleCellStyle}>
        <div>{fmtRateGridCell(branch.rate, branch.rateBasis)}</div>
        <div style={{ color: "#6b7280", fontSize: 10 }}>
          {branch.rateBasis === "items" ? "per item" : branch.rateBasis === "dispenser-count" ? "per dispenser" : "—"}
        </div>
      </PlainCell>

      <PlainCell style={middleCellStyle}>
        {branch.insightsCount > 0
          ? <>
              <span style={{ fontWeight: 600 }}>{branch.insightsCount}</span>
              {branch.topInsightTitle && (
                <div style={{ color: "#6b7280", fontSize: 10, lineHeight: 1.3 }}>
                  {branch.topInsightTitle.length > 32 ? branch.topInsightTitle.slice(0, 32) + "…" : branch.topInsightTitle}
                </div>
              )}
            </>
          : "—"
        }
      </PlainCell>

      <PlainCell align="center" style={middleCellStyle}>
        {branch.alertsCount > 0
          ? <span style={{ background: "#fee2e2", color: "#7f1d1d", padding: "2px 6px", borderRadius: 8, fontWeight: 700 }}>
              {branch.alertsCount}
            </span>
          : "—"
        }
      </PlainCell>

      <PlainCell align="center" style={middleCellStyle}>
        {branch.pmiCount > 0
          ? <span style={{ background: "#fee2e2", color: "#7f1d1d", padding: "2px 6px", borderRadius: 8, fontWeight: 600 }}
                  title={branch.maxHarmLevel ? `Worst: ${branch.maxHarmLevel.toLowerCase().replace(/_/g, " ")}` : ""}>
              {branch.pmiCount}
            </span>
          : "—"
        }
      </PlainCell>

      <PlainCell style={middleCellStyle}>
        <div style={{ display: "grid", gridTemplateColumns: "auto auto", gap: "0 6px", fontSize: 10, color: "#6b7280" }}>
          <span>Check:</span><span style={{ color: "#1f2937" }}>{fmtPctOrDash(branch.checkerPopulatedRate)}</span>
          <span>Cause:</span><span style={{ color: branch.causeCompletionRate !== null && branch.causeCompletionRate < 1 ? "#92400e" : "#1f2937" }}>{fmtPctOrDash(branch.causeCompletionRate)}</span>
          <span>LASA:</span><span style={{ color: branch.lasaFlagRate > 0.05 ? "#92400e" : "#1f2937" }}>{fmtPctOrDash(branch.lasaFlagRate)}</span>
          <span>Batch:</span><span style={{ color: branch.batchReporting ? "#92400e" : "#1f2937" }}>{branch.batchReporting ? "Yes" : "No"}</span>
        </div>
      </PlainCell>

      <PlainCell align="center" style={lastCellStyle}>
        <button
          onClick={onAction}
          style={{
            fontSize: 11, padding: "4px 10px",
            border: "1px solid #9ca3af", borderRadius: 4,
            background: "#fff", cursor: "pointer"
          }}
        >
          Actions
        </button>
      </PlainCell>
    </tr>
  );
}

function ActionsModal({ branch, onClose }) {
  const masked = useMaskPharmacyNames();
  // Stage 2 placeholder: action templates ready to wire to mailer in stage 3
  const templates = [
    {
      id: "encourage-tool",
      label: "Encourage tool usage",
      condition: branch.healthStatus === "non-user",
      subject: `Pharmace insights — getting started at ${branch.pharmacyName}`,
      body:
`Hello,

We've noticed that ${branch.pharmacyName} hasn't recorded any near-misses recently.

The Pharmace insight engine becomes useful once incident reporting is active — there's a lot of safety value in capturing every near-miss, even the small ones. Could the team have a quick conversation about logging routine?

I'd be happy to walk through the recording workflow if it would help.

Best regards,
[Your name]`
    },
    {
      id: "monthly-review-reminder",
      label: "Monthly review reminder",
      condition: branch.monthlyReportCompletionRate < 0.75,
      subject: `Pharmace — monthly review at ${branch.pharmacyName}`,
      body:
`Hello,

A quick reminder that ${branch.pharmacyName} has completed ${branch.monthlyReportCount12mo} of the last 12 monthly reviews.

The monthly review is where the team's qualitative context joins the data — the patterns the engine surfaces only become actionable when combined with what the team knows from the bench.

If there are practical obstacles, please let me know — happy to help streamline.

Best regards,
[Your name]`
    },
    {
      id: "alert-action",
      label: "Act on pending alerts",
      condition: branch.alertsCount > 0,
      subject: `Pharmace — ${branch.alertsCount} pending alert${branch.alertsCount === 1 ? "" : "s"} at ${branch.pharmacyName}`,
      body:
`Hello,

The Pharmace insight engine has ${branch.alertsCount} pending alert${branch.alertsCount === 1 ? "" : "s"} for ${branch.pharmacyName} that haven't been actioned yet.

Could you please review them in the Action Centre and either send through to the appropriate team member, or mark as actioned if handled offline?

Best regards,
[Your name]`
    },
    {
      id: "monthly-summary",
      label: "Monthly summary with group comparison",
      condition: branch.healthStatus !== "non-user",
      subject: `Pharmace — ${branch.pharmacyName} monthly summary`,
      body:
`Hello,

A quick summary of ${branch.pharmacyName} for the last completed month:

${branch.narrative || "No narrative available."}

This is one of the views the group insight engine produces — full detail is available in the dashboard.

Best regards,
[Your name]`
    }
  ].filter(t => t.condition);

  const [picked, setPicked] = useState(templates[0]?.id || null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [sentNote, setSentNote] = useState("");

  useEffect(() => {
    const t = templates.find(t => t.id === picked);
    if (t) {
      setSubject(t.subject);
      setBody(t.body);
    }
  }, [picked]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(720px, 95vw)", maxHeight: "92vh",
          background: "#fff", borderRadius: 8, display: "flex", flexDirection: "column",
          boxShadow: "0 20px 40px rgba(0,0,0,0.2)"
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Actions for {displayName(branch.pharmacyName, masked)}</div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>
            <StatusBadge status={branch.healthStatus} /> — {branch.statusReason}
          </div>
        </div>

        <div style={{ padding: 18, overflow: "auto", flex: 1 }}>
          {templates.length === 0 ? (
            <div style={{ fontSize: 13, color: "#6b7280", padding: 20, textAlign: "center" }}>
              No suggested actions for this branch right now. Everything looks fine.
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, fontWeight: 600, color: "#374151", marginBottom: 8 }}>
                Suggested actions
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
                {templates.map(t => (
                  <label key={t.id} style={{
                    display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                    padding: "6px 10px", borderRadius: 4,
                    background: picked === t.id ? "#eff6ff" : "transparent",
                    border: `1px solid ${picked === t.id ? "#93c5fd" : "transparent"}`
                  }}>
                    <input
                      type="radio"
                      checked={picked === t.id}
                      onChange={() => setPicked(t.id)}
                    />
                    <span style={{ fontSize: 12 }}>{t.label}</span>
                  </label>
                ))}
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                  style={{ width: "100%", padding: "6px 8px", fontSize: 13, border: "1px solid #d1d5db", borderRadius: 4 }}
                />
              </div>

              <div style={{ marginBottom: 10 }}>
                <label style={{ display: "block", fontSize: 11, fontWeight: 600, marginBottom: 4 }}>Body</label>
                <textarea
                  value={body}
                  onChange={e => setBody(e.target.value)}
                  rows={10}
                  style={{
                    width: "100%", padding: "8px", fontSize: 13, fontFamily: "inherit",
                    border: "1px solid #d1d5db", borderRadius: 4, resize: "vertical"
                  }}
                />
              </div>

              <div style={{
                fontSize: 12, color: "#92400e", padding: "10px 12px",
                background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 4,
                marginBottom: 10
              }}>
                <strong>Email is currently disabled.</strong> The button below marks this branch
                as actioned for record-keeping but does not send any mail.
              </div>

              {sentNote && (
                <div style={{ fontSize: 12, color: "#065f46", marginBottom: 10 }}>{sentNote}</div>
              )}
            </>
          )}
        </div>

        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button
            onClick={onClose}
            style={{
              fontSize: 12, padding: "6px 14px",
              border: "1px solid #9ca3af", borderRadius: 4,
              background: "#f3f4f6", cursor: "pointer"
            }}
          >
            Close
          </button>
          {templates.length > 0 && (
            <button
              onClick={() => setSentNote("Marked as actioned (email disabled).")}
              style={{
                fontSize: 12, padding: "6px 16px",
                border: "1px solid #1f2937", borderRadius: 4,
                background: "#1f2937", color: "#fff", fontWeight: 500, cursor: "pointer"
              }}
            >
              Mark as actioned
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function TrendGraph({ organisationName, pharmacyId, onBarClick }) {
  const [loading, setLoading] = useState(true);
  const [series, setSeries] = useState([]);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const token = localStorage.getItem("authToken");
        if (!token) { setLoading(false); return; }

        // Body assembly: pharmacyId wins over organisationName on the
        // server (a branch belongs to at most one org), so it's safe to
        // include only what the parent supplies.
        const body = {};
        if (pharmacyId) body.pharmacyId = pharmacyId;
        else if (organisationName) body.organisationName = organisationName;

        const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_analysis_trend", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        if (!res.ok) { if (!cancelled) { setError(`HTTP ${res.status}`); setLoading(false); } return; }
        const json = await res.json();
        if (cancelled) return;
        setSeries(json.series || []);
        setError("");
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [organisationName, pharmacyId]);

  if (loading) return <div style={{ fontSize: 12, color: "#6b7280" }}>Loading trend…</div>;
  if (error) return <div style={{ fontSize: 12, color: "#dc2626" }}>Trend error: {error}</div>;
  if (!series.length) return <div style={{ fontSize: 12, color: "#6b7280" }}>No trend data yet.</div>;

  // Compute the max total to scale bars
  const maxTotal = Math.max(...series.map(s => s.totalIncidents || 0), 1);

  // Top 5 categories across the series for stacking. Org-scope views
  // return empty totalIncidentsByCategory (the engine only stores
  // categories at fleet level), so topCats may end up empty — in which
  // case we render a single solid bar per month and hide the legend.
  const catTotals = {};
  for (const s of series) {
    for (const [cat, n] of Object.entries(s.totalIncidentsByCategory || {})) {
      catTotals[cat] = (catTotals[cat] || 0) + n;
    }
  }
  const topCats = Object.entries(catTotals).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([c]) => c);
  const hasCategories = topCats.length > 0;
  const palette = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#9ca3af"];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 220, padding: "10px 0", borderBottom: "1px solid #e5e7eb" }}>
        {series.map((s, i) => {
          const totalH = ((s.totalIncidents || 0) / maxTotal) * 180;
          const cats = s.totalIncidentsByCategory || {};
          const isClickable = !!onBarClick;
          return (
            <div
              key={i}
              style={{
                flex: 1, display: "flex", flexDirection: "column",
                justifyContent: "flex-end", alignItems: "center", minWidth: 30,
                cursor: isClickable ? "pointer" : "default"
              }}
              onClick={isClickable ? () => onBarClick({ year: s.year, month: s.month, monthLabel: s.monthLabel }) : undefined}
              title={isClickable ? `Click to see ${s.monthLabel || "this month"}'s error breakdown` : `${s.monthLabel}: ${s.totalIncidents} total`}
            >
              {/* Count label above the bar — always visible, even at zero. */}
              <div style={{
                fontSize: 10,
                fontWeight: 600,
                color: "#1f2937",
                marginBottom: 2,
                minHeight: 12
              }}>
                {s.totalIncidents || 0}
              </div>
              <div style={{ display: "flex", flexDirection: "column-reverse", width: "70%", height: totalH }}>
                {hasCategories ? (
                  topCats.map((cat, ci) => {
                    const h = ((cats[cat] || 0) / (s.totalIncidents || 1)) * totalH;
                    return (
                      <div key={cat}
                        style={{ background: palette[ci % palette.length], height: h, borderTop: ci > 0 ? "1px solid #fff" : "none" }}
                        title={`${cat}: ${cats[cat] || 0}`}
                      />
                    );
                  })
                ) : (
                  // Fallback when no category breakdown is available
                  // (e.g. organisation-scope views — the engine stores
                  // categories at fleet level only). Render a single
                  // solid bar so the trend shape is still legible.
                  <div style={{ background: palette[0], height: "100%" }} />
                )}
              </div>
              <div style={{ fontSize: 9, color: "#6b7280", marginTop: 4, transform: "rotate(-30deg)", whiteSpace: "nowrap", transformOrigin: "left top" }}>
                {(s.monthLabel || "").slice(0, 6)}
              </div>
            </div>
          );
        })}
      </div>
      {hasCategories && (
        <div style={{ display: "flex", gap: 14, marginTop: 24, fontSize: 11, color: "#374151", flexWrap: "wrap" }}>
          {topCats.map((cat, ci) => (
            <span key={cat} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
              <span style={{ display: "inline-block", width: 10, height: 10, background: palette[ci % palette.length] }} />
              {(cat || "").replace(/_/g, " ").toLowerCase()}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Modal that shows the per-errorTypeId histogram for one month within a
 * chosen scope (fleet / organisation / branch). Opened by clicking one
 * of the bars in the trend graph.
 *
 * Props:
 *   - target: { year, month, monthLabel } — the month to break down
 *   - organisationName, pharmacyId — scope (same as TrendGraph)
 *   - onClose — close handler
 */
function MonthBreakdownModal({ target, organisationName, pharmacyId, onClose }) {
  const masked = useMaskPharmacyNames();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Not authenticated. Please log in.");
        const body = { year: target.year, month: target.month };
        if (pharmacyId) body.pharmacyId = pharmacyId;
        else if (organisationName) body.organisationName = organisationName;

        const res = await fetch("https://api.pharmacegroup.co.uk/near_miss_analysis_month_breakdown", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const json = await res.json();
        if (cancelled) return;
        setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [target.year, target.month, organisationName, pharmacyId]);

  // Scope label for the heading — describes whose breakdown this is.
  const scopeLabel = pharmacyId
    ? (displayName(data?.pharmacyName, masked) || "branch")
    : (displayName(organisationName, masked) || "all sites");

  // Max for horizontal bar scaling
  const maxCount = data?.categories?.length
    ? Math.max(...data.categories.map(c => c.count), 1)
    : 1;
  const palette = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#0ea5e9", "#a855f7", "#14b8a6", "#f97316", "#6366f1"];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
        display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          width: "min(640px, 95vw)", maxHeight: "85vh",
          display: "flex", flexDirection: "column",
          background: "#fff", borderRadius: 8, overflow: "hidden",
          boxShadow: "0 10px 30px rgba(0,0,0,0.2)"
        }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #eee" }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: "#1f2937" }}>
            Errors — {target.monthLabel || `${target.year}-${target.month}`}
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
            {scopeLabel}{data ? ` · ${data.total} total` : ""}
          </div>
        </div>

        <div style={{ padding: "16px 18px", overflowY: "auto", flex: 1 }}>
          {loading && (
            <div style={{ fontSize: 13, color: "#6b7280" }}>Loading breakdown…</div>
          )}
          {error && (
            <div style={{ fontSize: 13, color: "#dc2626" }}>Error: {error}</div>
          )}
          {!loading && !error && data && data.categories.length === 0 && (
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              No incidents recorded for this month.
            </div>
          )}
          {!loading && !error && data && data.categories.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {data.categories.map((c, i) => {
                const pct = (c.count / maxCount) * 100;
                const totalPct = data.total > 0 ? Math.round((c.count / data.total) * 100) : 0;
                return (
                  <div key={c.errorTypeId} style={{ display: "grid", gridTemplateColumns: "minmax(120px, 30%) 1fr auto", gap: 10, alignItems: "center" }}>
                    <div
                      style={{ fontSize: 12, color: "#1f2937", wordBreak: "break-word" }}
                      title={c.errorTypeId}
                    >
                      {(c.errorTypeId || "(uncategorised)").replace(/_/g, " ")}
                    </div>
                    <div style={{ position: "relative", height: 18, background: "#f3f4f6", borderRadius: 4 }}>
                      <div style={{
                        position: "absolute", top: 0, left: 0, bottom: 0,
                        width: `${pct}%`,
                        background: palette[i % palette.length],
                        borderRadius: 4
                      }} />
                    </div>
                    <div style={{ fontSize: 12, color: "#374151", fontVariantNumeric: "tabular-nums", textAlign: "right", minWidth: 70 }}>
                      {c.count} <span style={{ color: "#9ca3af" }}>({totalPct}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div style={{ padding: "10px 18px", borderTop: "1px solid #eee", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              fontSize: 12, padding: "6px 14px",
              border: "1px solid #9ca3af", borderRadius: 4,
              background: "#f3f4f6", cursor: "pointer"
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}


const Mockupp = () => {
  const eventSourceRef = useRef(null);
  const [searchText, setSearchText] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isStreaming, setIsStreaming] = useState(false);
  const [pharmacies, setPharmacies] = useState([]);
  const [popupData, setPopupData] = useState(null);
  const [dateMode] = useState("today");
  const todayStr = new Date().toISOString().split("T")[0];
  const [customFrom, setCustomFrom] = useState(todayStr);
  const [customTo, setCustomTo] = useState(todayStr);

  // Pharmacy-name masking (admin toggle for demos/screen sharing).
  // Read once here so JSX in this component can read `masked` directly;
  // the hook also subscribes to live changes from the MaskToggleButton.
  const masked = useMaskPharmacyNames();

  // --- Create Group (admin-only display for "Pharmace all") ---
  const [createOpen, setCreateOpen] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");

  const orgName = localStorage.getItem("organisationName") || "";
  const canCreateGroup = orgName === "Pharmace all";

  const sortedPharmacies = useMemo(() => {
    if (!sortConfig.key) return pharmacies;

    return [...pharmacies].sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      const numA = parseFloat(String(valA).replace(/[%£,]/g, ""));
      const numB = parseFloat(String(valB).replace(/[%£,]/g, ""));

      if (!isNaN(numA) && !isNaN(numB)) {
        return sortConfig.direction === "asc" ? numA - numB : numB - numA;
      }

      return sortConfig.direction === "asc"
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [pharmacies, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }

      return { key, direction: "asc" };
    });
  };

  const getDateRange = () => {
    if (!customFrom || !customTo) {
      alert("Please select both From and To dates.");
      return null;
    }

    const fromParsed = new Date(customFrom);
    const toParsed = new Date(customTo);

    if (toParsed < fromParsed) {
      alert("'To' date must be after 'From' date.");
      return null;
    }

    fromParsed.setHours(0, 1, 0, 0);
    toParsed.setHours(23, 59, 59, 999);

    return { fromDate: fromParsed, toDate: toParsed };
  };

  // Visibility for section group headings
  // === Section visibility state + persistence (no reload) ===
  const [showScan, setShowScan] = useState(() => localStorage.getItem("scanToShelf") !== "false");
  const [showInventory, setShowInventory] = useState(() => localStorage.getItem("inventory") !== "false");
  const [showHousekeeping, setShowHousekeeping] = useState(() => localStorage.getItem("housekeeping") !== "false");
  const [showActivities, setShowActivities] = useState(() => localStorage.getItem("activities") !== "false");
  const [showCommunications, setShowCommunications] = useState(() => localStorage.getItem("communications") !== "false");

  const persistToggle = (key, setter) => (e) => {
    const val = !!e.target.checked;
    setter(val);
    localStorage.setItem(key, val ? "true" : "false");
  };

  /* UPDATED: Communications now has 12 columns (27–38 inclusive) */
  const sectionVisibility = [
    ...Array(10).fill(showScan),           // 0–9
    ...Array(3).fill(showInventory),       // 10–12
    ...Array(6).fill(showHousekeeping),    // 13–18
    ...Array(8).fill(showActivities),      // 19–26
    ...Array(21).fill(showCommunications)  // 27–38
  ];

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.onmessage = null;
        eventSourceRef.current.onerror = null;
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
    };
  }, []);

  const fetchData = async () => {
    const token = localStorage.getItem("authToken");

    if (!token) {
      alert("Not authenticated. Please log in.");
      return;
    }

    const range = getDateRange();

    if (!range) return;

    const members = JSON.parse(localStorage.getItem("members") || "[]");
    const organisationName = localStorage.getItem("organisationName") || "";

    let pharmacyNames;

    if (organisationName === "Pharmace all") {
      pharmacyNames = [{ pharmacyName: "xxx", pharmacyId: "xxx" }];
    } else {
      pharmacyNames = members.map((m) => ({
        pharmacyId: m.pharmacyId,
        pharmacyName: m.pharmacyName || m.pharmacyId
      }));
    }

    setPharmacies([]);
    setProgress(0);
    setIsStreaming(true);
    setLoading(false);

    try {
      const response = await fetch("https://api.pharmacegroup.co.uk/gridpopulate-stream", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          fromDate: range.fromDate.toISOString(),
          toDate: range.toDate.toISOString(),
          pharmacyNames
        })
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();

        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const parts = buffer.split("\n\n");
        buffer = parts.pop(); // leave incomplete segment for next round

        for (const part of parts) {
          if (part.startsWith("data: ")) {
            const json = JSON.parse(part.replace("data: ", ""));

            if (json.progress !== undefined) setProgress(json.progress);

            if (json.grid) {
              setPharmacies((json.grid || []).sort((a, b) =>
                a.pharmacyName.localeCompare(b.pharmacyName)
              ));

              setProgress(100);
              setIsStreaming(false);
              return; // Done
            }
          }
        }
      }

      setIsStreaming(false);
    } catch (err) {
      console.error("Error during fetch:", err);
      setIsStreaming(false);
    }
  };

  // Cell colouring (incl. SIM rules on "Sim-del")
  const getCellStyle = (columnName, value, pharmacy) => {
    // Shelf / Fridge / Cleaning traffic-lighting

    if (columnName === "Near Miss" || columnName === "Near Miss in 30 days") {
      // treat null/undefined/missing as stale too
      if (!Number(pharmacy?.nearMissReviewFresh)) {
        return { backgroundColor: "gold" }; // amber
      }
    }

    if (columnName === "Shelf Check" || columnName === "Fridge" || columnName === "Cleaning") {
      if (value === "OK") return { backgroundColor: "lightgreen" };
      if (value === "!") return { backgroundColor: "gold" };
      if (value === "!!") return { backgroundColor: "coral" };
    }

    // Handover = number
    if (columnName === "Handover") {
      const numericVal = Number(value);

      if (!isNaN(numericVal)) {
        if (numericVal === 0) return { backgroundColor: "lightgreen" };
        if (numericVal > 0) return { backgroundColor: "coral" };
      }
    }

    // Green for positive counts in these columns
    const greenIfPositive = ["SMS Sent", "Email Sent", "Scanned", "Collections", "Del-items","Del-pats","Walk-ins","Near Miss", "Near Miss in 30 days"];

    if (greenIfPositive.includes(columnName)) {
      const numericVal = Number(value);

      if (!isNaN(numericVal) && numericVal > 0) {
        return { backgroundColor: "lightgreen" };
      }

      // Custom logic if 'Scanned' > 5 for comms cells
      const scannedVal = Number(pharmacy["scanned"]);
      const smsSentVal = Number(pharmacy["smsSent"]);
      const emailSentVal = Number(pharmacy["emailSent"]);

      if (!isNaN(scannedVal) && scannedVal > 5) {
        if (columnName === "SMS Sent") {
          if (smsSentVal > 5) return { backgroundColor: "lightgreen" };
          if (smsSentVal === 0 && emailSentVal === 0) return { backgroundColor: "lightcoral" };
        }

        if (columnName === "Email Sent") {
          if (emailSentVal > 5) return { backgroundColor: "lightgreen" };
          if (smsSentVal === 0 && emailSentVal === 0) return { backgroundColor: "lightcoral" };
        }
      }
    }

    // Percentage gradient (kept)
    const percentGradientFields = ["SMS Signup", "Email Signup", "% msg sent"];

    if (percentGradientFields.includes(columnName)) {
      const percent = Number(String(value).replace("%", "").trim());

      if (!isNaN(percent) && percent > 0 && percent <= 100) {
        const r = Math.round(240 - (percent * (240 - 144)) / 100);
        const g = Math.round(128 + (percent * (255 - 128)) / 100);
        const b = Math.round(128 - (percent * 128) / 100);

        return { backgroundColor: `rgb(${r}, ${g}, ${b})` };
      }
    }

    // SIM delivery logic on the "Sim-del" cell
    if (columnName === "Sim-del") {
      const delivered = Number(pharmacy?.deliveredCount ?? 0);
      const waiting = Number(pharmacy?.waitingCount ?? 0);
      const other = Number(pharmacy?.otherCount ?? 0);
      const backend = Number(pharmacy?.backendSentCount ?? 0);

      if (delivered > 0) return { backgroundColor: "lightgreen" };
      if (delivered === 0 && other > 0) return { backgroundColor: "lightcoral" };
      if (delivered === 0 && other === 0 && waiting > 0) return { backgroundColor: "lightcoral" };
      if (backend > 0) return { backgroundColor: "lightcoral" };
    }

    // Flag "Use SIM msg" red if it's No while "No. SIM" > 0
    if (columnName === "Use SIM msg") {
      // value is the raw cell value for this column (boolean or "Yes"/"No")
      const useSim =
        typeof value === "boolean"
          ? value
          : String(value).toLowerCase() === "yes";

      // sim_number is the data key for the "No. SIM" column (see columnKeyMap)
      const noSimCount = Number(pharmacy?.sim_number ?? 0);

      if (!useSim && noSimCount > 0) {
        return { backgroundColor: "red", color: "white" };
      }
    }

    return { backgroundColor: "white" };
  };

  // Render helper: shows booleans as Yes/No, keeps others as-is or "-"
  const displayVal = (v) => (typeof v === "boolean" ? (v ? "Yes" : "No") : (v ?? "-"));

  const getPharmacyHealth = (pharmacy) => {
    let hasRed = false;
    let hasAmber = false;

    columnKeyMap.forEach((key, index) => {
      if (!sectionVisibility[index]) return;

      const label = columnLabels[index];
      const raw = pharmacy[key];
      const style = getCellStyle(label, raw, pharmacy);
      const bg = String(style?.backgroundColor || "").toLowerCase();

      if (bg === "red" || bg === "lightcoral" || bg === "coral") {
        hasRed = true;
      }

      if (bg === "gold") {
        hasAmber = true;
      }
    });

    if (hasRed) return { icon: "🔴", label: "Action needed", className: "health-red" };
    if (hasAmber) return { icon: "🟠", label: "Attention", className: "health-amber" };
    return { icon: "🟢", label: "OK", className: "health-green" };
  };

  const handleCellClick = async (columnIndex, pharmacy) => {
    setLoading(true);

    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        alert("Not authenticated. Please log in.");
        return;
      }

      const columnName = columnLabels[columnIndex] || `Column ${columnIndex + 1}`;
      const baseRange = getDateRange();

      if (!baseRange) {
        alert("Please select a valid date range.");
        return;
      }

      const { fromDate: baseFrom, toDate: baseTo } = baseRange;
      let reqFrom = new Date(baseFrom);
      let reqTo = new Date(baseTo);

      // --- existing click handlers (unchanged) ---

      if (["Scanned", "Collections"].includes(columnName)) {
        try {
          const route = columnName === "Scanned" ? "/scanneddetails" : "/collecteddetails";

          const response = await fetch(`https://api.pharmacegroup.co.uk${route}`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyName: pharmacy.pharmacyName,
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });

          if (!response.ok) throw new Error(`Failed to fetch ${columnName.toLowerCase()} data`);

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            chartData: result.data,
            isScannedOrCollected: true
          });

          return;
        } catch (err) {
          console.error(`Error fetching ${columnName.toLowerCase()} data:`, err);
        }
      }

      if (columnName === "Walk-ins") {
        try {
          const response = await fetch(`https://api.pharmacegroup.co.uk/walkindetails`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyName: pharmacy.pharmacyName,
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });

          if (!response.ok) throw new Error(`Failed to fetch ${columnName.toLowerCase()} data`);

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            chartData: result.data,
            isWalkin: true
          });

          return;
        } catch (err) {
          console.error(`Error fetching ${columnName.toLowerCase()} data:`, err);
        }
      }

      // NEW: Bulk messages (both "Bulk waiting" and "Bulk sent")
      if (columnName === "Bulk waiting" || columnName === "Bulk sent") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/bulkdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              dbName: pharmacy.pharmacyId,              // your grid stores dbName in pharmacyId
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });

          if (!response.ok) throw new Error(`Failed to fetch ${columnName.toLowerCase()} data`);

          const result = await response.json();

          // PopupGrid looks for p.data (among other shapes), so provide the array as `data`
          // result.details shape: [{ date, message, sent, waiting }, ...]
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            dbName: pharmacy.pharmacyId,
            data: result.details,
            isBulk: true
          });

          return;
        } catch (err) {
          console.error(`Error fetching ${columnName.toLowerCase()} data:`, err);
        }
      }

      if (columnName === "Near Miss in 30 days") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/nearmissdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });

          if (!response.ok) throw new Error("Failed to fetch near miss data");

          const result = await response.json();

          // NEAR MISS SPLIT: include pharmacyId/dbName so the right panel can call the monthly-report API
          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            pharmacyId: pharmacy.pharmacyId,
            dbName: pharmacy.pharmacyId,
            nearMissDetails: result.details,
            isNearMissPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching near miss data:", err);
        }
      }

      if (columnName === "Del-pats") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/deliverydetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyId: pharmacy.pharmacyId,
              pharmacyName: pharmacy.pharmacyName,
              fromDate: reqFrom.toISOString(),
              toDate: reqTo.toISOString()
            })
          });

          const result = await response.json();

          if (!response.ok) {
            console.error("Error fetching delivery details:", result);
            throw new Error(result?.message || "Failed to fetch delivery details");
          }

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            deliveryDetails: result,
            isDeliveryPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching delivery details:", err);
        }
      }

      if (columnName === "Accident book") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/accidentdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });

          if (!response.ok) throw new Error("Failed to fetch accident data");

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            accidentDetails: result.accidents,
            isAccidentPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching accident data:", err);
        }
      }

      if (columnName === "Handover") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/handoverdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            handoverDetails: result.details,
            isHandoverPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching handover details:", err);
        }
      }

      if (columnName === "Cleaning") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/cleaninglogdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            cleaningDetails: result.details,
            isCleaningPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching cleaning details:", err);
        }
      }

      if (columnName === "Shelf Check") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/shelfcheckdetails", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });

          if (!response.ok) throw new Error("Failed to fetch shelf check data");

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            shelfcheckdetails: result.shelfcheckdetails,
            isShelfCheckPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching shelf check details:", err);
        }
      }

      if (columnName === "Fridge") {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/fridgelogs", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({ pharmacyName: pharmacy.pharmacyName })
          });

          if (!response.ok) throw new Error("Failed to fetch fridge logs");

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            fridgeLogs: result.logs,
            isFridgePopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching fridge logs:", err);
        }
      }

      if (["Bags on shelves", "Bags under 1mth", "Bags over 1mth"].includes(columnName)) {
        let bucket = "all";

        if (columnName === "Bags under 1mth") bucket = "under";
        if (columnName === "Bags over 1mth") bucket = "over";

        const payload = { pharmacyId: pharmacy.pharmacyId, bucket };

        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/bagsonshelf_details", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify(payload)
          });

          const text = await response.text();

          if (!response.ok) throw new Error(`Shelf breakdown failed (${response.status})`);

          let result = {};

          try {
            result = JSON.parse(text);
          } catch (e) {
            console.error("JSON parse error:", e, "raw:", text);
            throw e;
          }

          const shelves = (result.shelves || []).sort((a, b) =>
            (a.description || "").localeCompare(b.description || "")
          );

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            shelfDetails: shelves,
            isShelfPopup: true,
            meta: result.meta
          });

          return;
        } catch (err) {
          console.error("Error fetching shelf breakdown:", err);
          alert("Couldn't load shelf breakdown. See console for details.");
        }
      }

      if (["Total stock £", "Exp/Slow value £"].includes(columnName)) {
        try {
          const response = await fetch("https://api.pharmacegroup.co.uk/stockbreakdown", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${token}`
            },
            body: JSON.stringify({
              pharmacyName: pharmacy.pharmacyName,
              expSlowOnly: columnName === "Exp/Slow value £"
            })
          });

          if (!response.ok) throw new Error("Failed to fetch stock data");

          const result = await response.json();

          setPopupData({
            column: columnName,
            pharmacyName: pharmacy.pharmacyName,
            stockDetails: result.details,
            isStockPopup: true
          });

          return;
        } catch (err) {
          console.error("Error fetching stock data:", err);
        }
      }

      // Default popup
      setPopupData({
        column: columnName,
        pharmacyName: pharmacy.pharmacyName,
        isShelfPopup: false
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateTotals = () => {
    const totals = {};

    columnKeyMap.forEach((key, index) => {
      let total = 0;
      let count = 0;

      pharmacies.forEach(pharmacy => {
        const val = pharmacy[key];

        if (typeof val === "number") {
          total += val;
          count++;
        } else if (typeof val === "string" && val.endsWith("%")) {
          const percent = parseFloat(val.replace("%", ""));

          if (!isNaN(percent)) {
            total += percent;
            count++;
          }
        }
      });

      const isPercentage = columnLabels[index].includes("Signup");

      if (count === 0) {
        totals[key] = "-";
      } else if (isPercentage) {
        totals[key] = `${Math.round(total / count)}%`;
      } else {
        totals[key] = total;
      }
    });

    return totals;
  };

  const handleCreateGroup = async () => {
    if (!newOrgName || !newEmail || !newPassword) {
      alert("Please complete all fields.");
      return;
    }

    // Require a valid login token (the endpoint returns 401 without it)
    const token = localStorage.getItem("authToken");

    if (!token) {
      alert("You must be logged in to create a group (no auth token found).");
      return;
    }

    try {
      const res = await fetch("https://api.pharmacegroup.co.uk/creategroup", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`,   // <-- add auth header
        },
        body: JSON.stringify({
          organisation_name: newOrgName,
          email: newEmail,
          password: newPassword,
          expireBefore: 4,
          slowBefore: 26,
          members: [],
          scanToShelf: true,
          inventory: true,
          housekeeping: true,
          activities: true,
          communications: true
        })
      });

      // Helpful error handling for 401s
      if (res.status === 401) {
        const txt = await res.text();
        throw new Error(txt || "Unauthorized (401). Your session may have expired.");
      }

      const text = await res.text();

      if (!res.ok) throw new Error(text || `HTTP ${res.status}`);

      alert("✅ Group created. You can now log in with that email/password.");
      setCreateOpen(false);
      setNewOrgName("");
      setNewEmail("");
      setNewPassword("");
    } catch (err) {
      console.error("Create group failed:", err);
      alert("Failed to create group: " + err.message);
    }
  };

  return (
    <div className="mockup-container">
      <div
        className="dashboard-header modern-header"
        style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
      >
        <div>
          <h2>Pharmacy Summary Dashboard</h2>
          <div className="dashboard-subtitle">
            High-density operational view across all branches
          </div>
        </div>

        {loading && !isStreaming && (
          <div className="loading-overlay">
            <div className="spinner"></div>
          </div>
        )}

        <div className="date-controls">
          <MaskToggleButton style={{ marginRight: "10px" }} />
          <label style={{ marginLeft: "10px" }}>
            From:
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} />
          </label>

          <label style={{ marginLeft: "10px" }}>
            To:
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} />
          </label>

          <button className="btn btn-primary" style={{ marginLeft: "10px" }} onClick={fetchData}>
            🔄 Refresh analysis
          </button>

          {canCreateGroup && (
            <button
              className="btn btn-secondary"
              style={{ marginLeft: "10px" }}
              onClick={() => setCreateOpen(true)}
              title="Create a new organisation"
            >
              ➕ Create group
            </button>
          )}
        </div>
      </div>

      {/* Section visibility checkboxes */}
      <div
        className="section-filters"
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: "12px 18px",
          alignItems: "center",
          margin: "10px 0"
        }}
      >
        <strong style={{ marginRight: 6 }}>Sections:</strong>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showScan} onChange={persistToggle("scanToShelf", setShowScan)} />
          <span>Scan to shelf</span>
        </label>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showInventory} onChange={persistToggle("inventory", setShowInventory)} />
          <span>Inventory</span>
        </label>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showHousekeeping} onChange={persistToggle("housekeeping", setShowHousekeeping)} />
          <span>Housekeeping</span>
        </label>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showActivities} onChange={persistToggle("activities", setShowActivities)} />
          <span>Activities</span>
        </label>

        <label style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
          <input type="checkbox" checked={showCommunications} onChange={persistToggle("communications", setShowCommunications)} />
          <span>Communications</span>
        </label>
      </div>

      <div className="search-container" style={{ margin: "10px 0" }}>
        <input
          type="text"
          className="search-box"
          placeholder="🔍 Search (all terms must match)..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{
            width: "60%",
            padding: "8px",
            fontSize: "14px",
            borderRadius: "6px",
            border: "1px solid #ccc"
          }}
        />
      </div>

      <div className="mockup-table modern-card">
        <table>
          <thead>
            <tr>
              <th className="sticky-col group-root-header">Pharmacy</th>

              {sectionVisibility.slice(0, 10).some(Boolean) && (
                <th className="group-label group-scan bg-light" colSpan={sectionVisibility.slice(0, 10).filter(Boolean).length}>
                  Scan to shelf
                </th>
              )}

              {sectionVisibility.slice(10, 13).some(Boolean) && (
                <th className="group-label group-inventory bg-dark" colSpan={sectionVisibility.slice(10, 13).filter(Boolean).length}>
                  Inventory
                </th>
              )}

              {sectionVisibility.slice(13, 19).some(Boolean) && (
                <th className="group-label group-housekeeping bg-light" colSpan={sectionVisibility.slice(13, 19).filter(Boolean).length}>
                  Housekeeping
                </th>
              )}

              {sectionVisibility.slice(19, 27).some(Boolean) && (
                <th className="group-label group-activities bg-dark" colSpan={sectionVisibility.slice(19, 27).filter(Boolean).length}>
                  Activities
                </th>
              )}

              {/* Communications spans indices 27..38 (12 cols) */}
              {sectionVisibility.slice(27, 46).some(Boolean) && (
                <th className="group-label group-communications bg-light" colSpan={sectionVisibility.slice(27, 39).filter(Boolean).length}>
                  Communications
                </th>
              )}
            </tr>

            <tr>
              <th className="sticky-col sticky-header"></th>

              {columnLabels.map((label, idx) =>
                sectionVisibility[idx] ? (
                  <th
                    key={idx}
                    className="sticky-header sortable-header"
                    onClick={() => requestSort(columnKeyMap[idx])}
                    style={{ cursor: "pointer" }}
                  >
                    {label}
                    {sortConfig.key === columnKeyMap[idx] && (sortConfig.direction === "asc" ? " ▲" : " ▼")}
                  </th>
                ) : null
              )}
            </tr>
          </thead>

          <tbody>
            {sortedPharmacies
              .filter(pharmacy => {
                const fragments = searchText.toLowerCase().split(" ").filter(f => f.trim() !== "");
                const searchable = Object.values(pharmacy).join(" ").toLowerCase();
                return fragments.every(fragment => searchable.includes(fragment));
              })
              .map((pharmacy, rowIndex) => {
                const health = getPharmacyHealth(pharmacy);

                return (
                  <tr key={rowIndex} className="data-row">
                    <td className="sticky-col pharmacy-cell">
                      <span className={`health-dot ${health.className}`} title={health.label}>
                        {health.icon}
                      </span>

                      <span className="pharmacy-name-text">
                        {displayName(pharmacy.pharmacyName, masked).replace(/pharmacy/i, "").trim()}
                      </span>
                    </td>

                    {columnKeyMap.map((key, colIndex) =>
                      sectionVisibility[colIndex]
                        ? (() => {
                            const raw = pharmacy[key];
                            const shown = displayVal(raw);
                            const cellClass = shown === "-" ? "cell pale" : "cell";

                            return (
                              <td
                                key={colIndex}
                                className={cellClass}
                                style={getCellStyle(columnLabels[colIndex], raw, pharmacy)}
                                onClick={() => handleCellClick(colIndex, pharmacy)}
                              >
                                {shown}
                              </td>
                            );
                          })()
                        : null
                    )}
                  </tr>
                );
              })}

            <tr className="total-row enhanced-total">
              <td className="sticky-col">Total</td>

              {columnKeyMap.map((key, colIndex) =>
                sectionVisibility[colIndex] ? (
                  <td key={colIndex} className="cell">
                    {displayVal(calculateTotals()[key])}
                  </td>
                ) : null
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {isStreaming && (
        <div className="streaming-progress" style={{ marginBottom: 10 }}>
          Loading data... {progress}%
          <progress value={progress} max="100" style={{ width: "100%" }} />
        </div>
      )}

      {createOpen && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.35)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setCreateOpen(false);
          }}
        >
          <div
            className="create-group-modal"
            style={{
              width: 420,
              maxWidth: "96vw",
              background: "#fff",
              borderRadius: 8,
              boxShadow: "0 20px 50px rgba(0,0,0,0.25)",
              overflow: "hidden",
              fontSize: 14
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 12px", borderBottom: "1px solid #e5e7eb", background: "#f9fafb" }}>
              <strong>Create New Group</strong>

              <button
                onClick={() => setCreateOpen(false)}
                style={{ background: "#111827", color: "#fff", border: "1px solid #111827", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}
              >
                Close
              </button>
            </div>

            <div style={{ padding: 12, display: "grid", gap: 10 }}>
              <label>
                <div style={{ marginBottom: 4 }}>Organisation name</div>
                <input
                  type="text"
                  value={newOrgName}
                  onChange={(e) => setNewOrgName(e.target.value)}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 4 }}>Email</div>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
              </label>

              <label>
                <div style={{ marginBottom: 4 }}>Password</div>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: "100%", padding: 8, border: "1px solid #cbd5e1", borderRadius: 6 }}
                />
              </label>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, padding: 12, borderTop: "1px solid #e5e7eb" }}>
              <button onClick={() => setCreateOpen(false)} className="btn btn-light">Cancel</button>

              <button
                onClick={handleCreateGroup}
                className="btn btn-primary"
                disabled={!newOrgName || !newEmail || !newPassword}
              >
                Create group
              </button>
            </div>
          </div>
        </div>
      )}

      {popupData && (
        <div className="popup">
          <div className="popup-content">
            <button className="close-btn" onClick={() => setPopupData(null)}>X</button>

            {/* NEAR MISS SPLIT: intercept Near Miss to show the 2-panel dialog */}
            {popupData?.isNearMissPopup ? (
              <NearMissSplitDialog popupData={popupData} />
            ) : (
              (popupData.isDeliveryPopup ||
                popupData.column === "Fridge" ||
                popupData.isStockPopup ||
                popupData.isShelfPopup ||
                popupData.isShelfCheckPopup ||
                popupData.isAccidentPopup ||
                popupData.isHandoverPopup ||
                popupData.isCleaningPopup ||
                popupData.isScannedOrCollected ||
                popupData.isWalkin ||
                popupData.column === "Near Miss" ||   // keep legacy path intact for safety
                popupData.isSmsBySimPopup) ? (
                <PopupContent popupData={popupData} />
              ) : (
                <PopupGrid popupData={popupData} />
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// SOP Compliance — page component (Phase 2)
// ============================================================================
// Mirrors NearMissAnalysis structure: top chart + grid + click-through modal
// + per-SOP drill-down. Adds: as-of date picker, groupBy toggle, browser-print
// PDF, CSV export, staff outliers list.

// ----- small helpers --------------------------------------------------------

// Red (0%) → amber (50%) → green (100%) cell background. Returns null-grey
// for null pct (no cards due → "—" cell).
function sopPctColor(pct) {
  if (pct === null || pct === undefined) return "#f3f4f6";
  // hue 0=red, 60=yellow, 120=green
  const hue = Math.max(0, Math.min(120, (pct / 100) * 120));
  return `hsl(${hue}, 75%, 82%)`;
}
function sopPctTextColor(pct) {
  if (pct === null || pct === undefined) return "#9ca3af";
  return pct < 50 ? "#7f1d1d" : pct < 80 ? "#78350f" : "#064e3b";
}

function sopFmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

function sopTodayInputValue() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// YYYY-MM-DD → end-of-day ISO so the picked date captures activity ON that day
function sopDateInputToIso(value) {
  if (!value) return new Date().toISOString();
  const d = new Date(value + "T23:59:59.999");
  if (isNaN(d)) return new Date().toISOString();
  return d.toISOString();
}

// CSV escape: wrap in quotes if needed, double internal quotes
function sopCsvCell(v) {
  if (v === null || v === undefined) return "";
  const s = String(v);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function sopDownloadCsv(filename, rows) {
  const csv = rows.map(r => r.map(sopCsvCell).join(",")).join("\r\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ----- main page ------------------------------------------------------------

function SOPCompliance() {
  // View state: null = summary view, "detail" = staff grid view
  const [view, setView] = useState(null);
  const [selectedPharmacyId, setSelectedPharmacyId] = useState(null);

  // Controls
  const [asOfInput, setAsOfInput] = useState(sopTodayInputValue());
  const asOfIso = sopDateInputToIso(asOfInput);

  // Grid data (for summary view)
  const [loadingGrid, setLoadingGrid] = useState(true);
  const [errorGrid, setErrorGrid] = useState("");
  const [gridData, setGridData] = useState(null);
  const [showActive, setShowActive] = useState(true);

  const tokenRef = useRef(null);
  useEffect(() => {
    tokenRef.current = localStorage.getItem("authToken");
  }, []);

  // Load grid data grouped by pharmacy
  const loadGrid = async () => {
    setLoadingGrid(true);
    setErrorGrid("");
    try {
      const token = tokenRef.current || localStorage.getItem("authToken");
      if (!token) throw new Error("Not authenticated");

      const res = await fetch("https://api.pharmacegroup.co.uk/sop_compliance_grid", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({
          asOfDate: asOfIso,
          groupBy: "pharmacy"
        })
      });

      if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
      const json = await res.json();
      setGridData(json);
    } catch (e) {
      setErrorGrid(e.message || String(e));
    } finally {
      setLoadingGrid(false);
    }
  };

  useEffect(() => {
    loadGrid();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asOfInput]);

  // Extract data
  const groups = gridData?.groups || [];
  const sopTitles = gridData?.sopTitles || [];

  // Build pharmacy summary rows
  const pharmacySummary = useMemo(() => {
    const rows = [];

    for (const group of groups) {
      let totalActiveSops = 0;
      let totalAssignments = 0;
      let totalRead = 0;

      const sopsWithAssignments = new Set();

      for (const sopTitle of sopTitles) {
        let sopHasAssignment = false;
        for (const staffName of group.headers) {
          const cell = group.cells?.[sopTitle]?.[staffName];
          if (cell) {
            sopHasAssignment = true;
            totalAssignments += cell.shouldHaveRead || 0;
            totalRead += cell.didRead || 0;
          }
        }
        if (sopHasAssignment) {
          sopsWithAssignments.add(sopTitle);
        }
      }

      totalActiveSops = sopsWithAssignments.size;
      const pctRead =
        totalAssignments > 0
          ? Math.round((totalRead / totalAssignments) * 1000) / 10
          : 0;

      rows.push({
        pharmacyId: group.key,
        pharmacyName: group.label,
        activeSops: totalActiveSops,
        assignments: totalAssignments,
        read: totalRead,
        pctRead: pctRead
      });
    }

    return rows.sort((a, b) => a.pharmacyName.localeCompare(b.pharmacyName));
  }, [groups, sopTitles]);

  // CSV export (summary)
  const exportCsv = () => {
    const rows = [["Pharmacy", "Active SOPs", "Assignments", "Read", "% Read"]];
    for (const row of pharmacySummary) {
      rows.push([
        row.pharmacyName,
        row.activeSops,
        row.assignments,
        row.read,
        row.pctRead
      ]);
    }
    const dateStr = asOfInput.replace(/-/g, "");
    sopDownloadCsv(`sop-compliance-summary_${dateStr}.csv`, rows);
  };

  const printPdf = () => {
    window.print();
  };

  // ========================================================================
  // VIEW 1: SUMMARY (Pharmacy-level grid)
  // ========================================================================

  if (view === null) {
    return (
      <div style={{ padding: 16, background: "#f9fafb", minHeight: "100vh" }}>
        <style>{`
          @media print {
            body { background: white !important; }
            .sop-no-print { display: none !important; }
            .sop-summary-table { font-size: 10pt; }
            .sop-summary-table th { font-size: 9pt; }
          }
        `}</style>

        <div className="sop-page">
          {/* Heading */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 12,
              marginBottom: 12
            }}
          >
            <div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: "#111827" }}>
                SOP Compliance
              </h2>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                Pharmacy summary as of {sopFmtDate(asOfIso)}
              </div>
            </div>

            {/* Controls bar */}
            <div
              className="sop-no-print"
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                flexWrap: "wrap"
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 13
                }}
              >
                <span style={{ color: "#374151" }}>As of:</span>
                <input
                  type="date"
                  value={asOfInput}
                  max={sopTodayInputValue()}
                  onChange={e => setAsOfInput(e.target.value)}
                  style={{
                    padding: "4px 8px",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    fontSize: 13
                  }}
                />
              </label>

              <div style={{ display: "flex", gap: 4 }}>
                <button
                  onClick={() => setShowActive(true)}
                  style={{
                    padding: "5px 12px",
                    background: showActive ? "#2d5a3d" : "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: "pointer",
                    color: showActive ? "#fff" : "#374151",
                    fontWeight: showActive ? 600 : 400,
                    transition: "all 0.2s"
                  }}
                >
                  Active pharmacies
                </button>
                <button
                  onClick={() => setShowActive(false)}
                  style={{
                    padding: "5px 12px",
                    background: !showActive ? "#2d5a3d" : "#f3f4f6",
                    border: "1px solid #d1d5db",
                    borderRadius: 4,
                    fontSize: 13,
                    cursor: "pointer",
                    color: !showActive ? "#fff" : "#374151",
                    fontWeight: !showActive ? 600 : 400,
                    transition: "all 0.2s"
                  }}
                >
                  Inactive pharmacies
                </button>
              </div>

              <button
                onClick={exportCsv}
                disabled={!gridData}
                style={{
                  padding: "5px 10px",
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: "pointer",
                  color: "#374151"
                }}
              >
                Export CSV
              </button>
              <button
                onClick={printPdf}
                disabled={!gridData}
                style={{
                  padding: "5px 10px",
                  background: "#fff",
                  border: "1px solid #d1d5db",
                  borderRadius: 4,
                  fontSize: 13,
                  cursor: "pointer",
                  color: "#374151"
                }}
              >
                Print / PDF
              </button>
            </div>
          </div>

          {/* Error state */}
          {errorGrid && (
            <div
              style={{
                padding: 16,
                color: "#dc2626",
                background: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: 6,
                marginBottom: 16
              }}
            >
              Error: {errorGrid}
            </div>
          )}

          {/* Loading state */}
          {loadingGrid && (
            <div style={{ padding: 24, color: "#6b7280" }}>Loading…</div>
          )}

          {/* Empty state */}
          {!loadingGrid && !errorGrid && 
            pharmacySummary.filter(row => showActive ? row.assignments > 0 : row.assignments === 0).length === 0 && (
            <div
              style={{
                padding: 24,
                color: "#6b7280",
                textAlign: "center",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8
              }}
            >
              {showActive 
                ? "No active pharmacies with SOP assignments."
                : "No inactive pharmacies (all pharmacies have assignments)."}
            </div>
          )}

          {/* Summary table */}
          {!loadingGrid && !errorGrid && pharmacySummary.length > 0 && (
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                overflow: "hidden"
              }}
            >
              <div style={{ overflowX: "auto" }}>
                <table
                  className="sop-summary-table"
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 13
                  }}
                >
                  <thead>
                    <tr style={{ background: "#f3f4f6" }}>
                      <th
                        style={{
                          padding: "10px 12px",
                          textAlign: "left",
                          borderBottom: "1px solid #e5e7eb",
                          fontWeight: 600,
                          color: "#374151"
                        }}
                      >
                        Pharmacy
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          textAlign: "center",
                          borderBottom: "1px solid #e5e7eb",
                          fontWeight: 600,
                          color: "#374151"
                        }}
                      >
                        Active SOPs
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          textAlign: "center",
                          borderBottom: "1px solid #e5e7eb",
                          fontWeight: 600,
                          color: "#374151"
                        }}
                      >
                        Assignments
                      </th>
                      <th
                        style={{
                          padding: "10px 12px",
                          textAlign: "center",
                          borderBottom: "1px solid #e5e7eb",
                          fontWeight: 600,
                          color: "#374151"
                        }}
                      >
                        % Read
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pharmacySummary
                      .filter(row => showActive ? row.assignments > 0 : row.assignments === 0)
                      .map((row) => (
                      <tr
                        key={row.pharmacyId}
                        onClick={() => {
                          setSelectedPharmacyId(row.pharmacyId);
                          setView("detail");
                        }}
                        style={{
                          cursor: "pointer",
                          borderBottom: "1px solid #f3f4f6",
                          transition: "background 0.2s"
                        }}
                        onMouseOver={(e) =>
                          (e.currentTarget.style.background = "#f9fafb")
                        }
                        onMouseOut={(e) =>
                          (e.currentTarget.style.background = "#fff")
                        }
                      >
                        <td
                          style={{
                            padding: "8px 12px",
                            color: "#111827",
                            fontWeight: 500
                          }}
                        >
                          {row.pharmacyName}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            textAlign: "center",
                            color: "#374151"
                          }}
                        >
                          {row.activeSops}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            textAlign: "center",
                            color: "#374151"
                          }}
                        >
                          {row.assignments}
                        </td>
                        <td
                          style={{
                            padding: "8px 12px",
                            textAlign: "center",
                            color: "#374151",
                            fontWeight: 600
                          }}
                        >
                          {row.pctRead}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ========================================================================
  // VIEW 2: DETAIL (Staff × SOP grid via SOPStaffGrid)
  // ========================================================================

  if (view === "detail" && selectedPharmacyId) {
    return (
      <SOPStaffGrid
        pharmacyId={selectedPharmacyId}
        asOfDate={asOfIso}
        showDates={false}
        onBack={() => {
          setView(null);
          setSelectedPharmacyId(null);
        }}
      />
    );
  }

  return null;
}


// ============================================================================
// SOPTrendChart — stacked bar (green=read, red=unread) per SOP title
// ============================================================================
function SOPTrendChart({ asOfIso, organisationName, pharmacyId, onBarClick }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Not authenticated");
        const body = { asOfDate: asOfIso };
        if (pharmacyId) body.pharmacyId = pharmacyId;
        else if (organisationName) body.organisationName = organisationName;

        const res = await fetch("https://api.pharmacegroup.co.uk/sop_compliance_trend", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [asOfIso, organisationName, pharmacyId]);

  if (loading) return <div style={{ fontSize: 12, color: "#6b7280", padding: 20 }}>Loading chart…</div>;
  if (error) return <div style={{ fontSize: 12, color: "#dc2626", padding: 12 }}>Chart error: {error}</div>;
  if (!data || !data.sops || data.sops.length === 0) {
    return <div style={{ fontSize: 12, color: "#6b7280", padding: 20 }}>No SOPs due in the selected scope.</div>;
  }

  const sops = data.sops;
  const maxTotal = Math.max(...sops.map(s => s.shouldHaveRead), 1);
  const reminderInfo = data.reminderEffectiveness;

  // Bar widths: keep them readable. Total chart width scales with SOP count.
  const barWidth = Math.max(28, Math.min(60, Math.floor(900 / sops.length)));
  const gap = 6;
  const chartHeight = 200;
  const labelArea = 110; // bottom area reserved for slanted SOP titles

  return (
    <div>
      <div style={{ overflowX: "auto" }}>
        <svg
          width={sops.length * (barWidth + gap) + 40}
          height={chartHeight + labelArea + 30}
          style={{ display: "block" }}
        >
          {/* Y axis line */}
          <line x1={30} y1={20} x2={30} y2={chartHeight + 20} stroke="#d1d5db" strokeWidth="1" />
          {/* Y axis ticks (0, max/2, max) */}
          {[0, 0.5, 1].map((f, i) => {
            const y = chartHeight + 20 - f * chartHeight;
            const v = Math.round(f * maxTotal);
            return (
              <g key={i}>
                <line x1={28} y1={y} x2={30} y2={y} stroke="#d1d5db" />
                <text x={26} y={y + 4} fontSize={10} fill="#6b7280" textAnchor="end">{v}</text>
              </g>
            );
          })}

          {sops.map((s, i) => {
            const x = 35 + i * (barWidth + gap);
            const totalH = (s.shouldHaveRead / maxTotal) * chartHeight;
            const readH = s.shouldHaveRead > 0 ? (s.didRead / s.shouldHaveRead) * totalH : 0;
            const unreadH = totalH - readH;
            const topY = chartHeight + 20 - totalH;
            return (
              <g key={s.title} style={{ cursor: onBarClick ? "pointer" : "default" }}
                 onClick={onBarClick ? () => onBarClick(s.title) : undefined}>
                {/* Unread (red) on top */}
                <rect x={x} y={topY} width={barWidth} height={unreadH} fill="#ef4444" />
                {/* Read (green) below */}
                <rect x={x} y={topY + unreadH} width={barWidth} height={readH} fill="#10b981" />

                {/* Total label above bar */}
                <text x={x + barWidth / 2} y={topY - 4} fontSize={10} fill="#374151" textAnchor="middle" fontWeight="600">
                  {s.didRead}/{s.shouldHaveRead}
                </text>
                {/* Percentage inside the bar if there's room */}
                {totalH > 30 && (
                  <text x={x + barWidth / 2} y={topY + totalH / 2 + 4} fontSize={11} fill="#fff" textAnchor="middle" fontWeight="600">
                    {s.pct}%
                  </text>
                )}
                {/* Slanted SOP title */}
                <text
                  x={x + barWidth / 2}
                  y={chartHeight + 30}
                  fontSize={10}
                  fill="#374151"
                  textAnchor="end"
                  transform={`rotate(-45, ${x + barWidth / 2}, ${chartHeight + 30})`}
                >
                  {s.title.length > 28 ? s.title.slice(0, 26) + "…" : s.title}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Legend + reminder effectiveness */}
      <div style={{ display: "flex", gap: 20, alignItems: "center", marginTop: 8, fontSize: 12, color: "#374151", flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, background: "#10b981", display: "inline-block", borderRadius: 2 }}></span>
          Read
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ width: 12, height: 12, background: "#ef4444", display: "inline-block", borderRadius: 2 }}></span>
          Not read
        </div>
        {onBarClick && (
          <div style={{ color: "#6b7280", fontStyle: "italic" }}>Click a bar to see 12-month trend for that SOP</div>
        )}
        {reminderInfo && reminderInfo.sent > 0 && (
          <div style={{ marginLeft: "auto", color: "#6b7280" }}>
            Reminders: {reminderInfo.sent} sent · {reminderInfo.signedWithin7d} signed within 7 days ({reminderInfo.pct}%)
          </div>
        )}
      </div>
    </div>
  );
}


// ============================================================================
// SOPCellModal — read/unread staff list for one (pharmacy × SOP × role) cell
// ============================================================================
function SOPCellModal({ asOfIso, target, onClose }) {
  const masked = useMaskPharmacyNames();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Not authenticated");
        const body = {
          asOfDate: asOfIso,
          sopTitle: target.sopTitle,
          roleName: target.roleName
        };
        if (target.pharmacyId) body.pharmacyId = target.pharmacyId;
        if (target.pharmacyName) body.pharmacyName = target.pharmacyName;

        const res = await fetch("https://api.pharmacegroup.co.uk/sop_compliance_cell_detail", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [asOfIso, target.sopTitle, target.roleName, target.pharmacyId, target.pharmacyName]);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(720px, 95vw)", maxHeight: "85vh", background: "#fff", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>{displayName(data?.pharmacyName || target.pharmacyName || target.pharmacyId, masked)}</div>
            <div style={{ fontSize: 13, color: "#374151", marginTop: 2 }}>{target.sopTitle}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>{target.roleName} · as of {sopFmtDate(asOfIso)}</div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, color: "#6b7280", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ overflowY: "auto", padding: 16 }}>
          {loading && <div style={{ color: "#6b7280" }}>Loading…</div>}
          {error && <div style={{ color: "#dc2626" }}>Error: {error}</div>}
          {!loading && !error && data && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#064e3b", marginBottom: 8 }}>
                  Read ({data.read?.length || 0})
                </div>
                {data.read?.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>No one has signed yet.</div>
                ) : (
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ textAlign: "left", padding: "4px 6px" }}>Name</th>
                        <th style={{ textAlign: "left", padding: "4px 6px" }}>Signed</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Took</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.read.map((r, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "4px 6px" }}>{displayName(r.staffName, masked)}</td>
                          <td style={{ padding: "4px 6px", color: "#6b7280" }}>{sopFmtDate(r.signedAt)}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", color: "#6b7280" }}>{r.daysToSign}d</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: "#7f1d1d", marginBottom: 8 }}>
                  Unread ({data.unread?.length || 0})
                </div>
                {data.unread?.length === 0 ? (
                  <div style={{ fontSize: 12, color: "#9ca3af", fontStyle: "italic" }}>Everyone has signed.</div>
                ) : (
                  <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ color: "#6b7280", borderBottom: "1px solid #e5e7eb" }}>
                        <th style={{ textAlign: "left", padding: "4px 6px" }}>Name</th>
                        <th style={{ textAlign: "left", padding: "4px 6px" }}>Due</th>
                        <th style={{ textAlign: "right", padding: "4px 6px" }}>Overdue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.unread.map((r, i) => (
                        <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                          <td style={{ padding: "4px 6px" }}>{displayName(r.staffName, masked)}</td>
                          <td style={{ padding: "4px 6px", color: "#6b7280" }}>{sopFmtDate(r.dueDate)}</td>
                          <td style={{ padding: "4px 6px", textAlign: "right", color: "#7f1d1d", fontWeight: 600 }}>
                            {r.daysOverdue !== null ? `${r.daysOverdue}d` : "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ============================================================================
// SOPPerSopTrendModal — 12-month trend for one SOP title, bar-click drill-down
// ============================================================================
function SOPPerSopTrendModal({ asOfIso, sopTitle, organisationName, pharmacyId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("authToken");
        if (!token) throw new Error("Not authenticated");
        const body = { sopTitle, asOfDate: asOfIso, months: 12 };
        if (pharmacyId) body.pharmacyId = pharmacyId;
        else if (organisationName) body.organisationName = organisationName;

        const res = await fetch("https://api.pharmacegroup.co.uk/sop_compliance_per_sop_trend", {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
          body: JSON.stringify(body)
        });
        if (!res.ok) throw new Error((await res.text()) || `HTTP ${res.status}`);
        const json = await res.json();
        if (!cancelled) setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [sopTitle, asOfIso, organisationName, pharmacyId]);

  const series = data?.series || [];
  const maxTotal = Math.max(...series.map(s => s.shouldHaveRead), 1);

  return (
    <div
      onClick={onClose}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{ width: "min(800px, 95vw)", maxHeight: "85vh", background: "#fff", borderRadius: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        <div style={{ padding: "14px 18px", borderBottom: "1px solid #e5e7eb", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 600, color: "#111827" }}>12-month trend</div>
            <div style={{ fontSize: 13, color: "#374151", marginTop: 2 }}>{sopTitle}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {pharmacyId ? "branch view" : organisationName ? organisationName : "fleet view"} · ending {sopFmtDate(asOfIso)}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", fontSize: 20, color: "#6b7280", cursor: "pointer" }}>×</button>
        </div>

        <div style={{ padding: 16, overflowY: "auto" }}>
          {loading && <div style={{ color: "#6b7280" }}>Loading 12-month trend… this may take a moment for fleet view.</div>}
          {error && <div style={{ color: "#dc2626" }}>Error: {error}</div>}

          {!loading && !error && series.length > 0 && (
            <div style={{ display: "flex", alignItems: "flex-end", gap: 6, height: 240, padding: "10px 0", borderBottom: "1px solid #e5e7eb" }}>
              {series.map((s, i) => {
                const totalH = (s.shouldHaveRead / maxTotal) * 180;
                const readH = s.shouldHaveRead > 0 ? (s.didRead / s.shouldHaveRead) * totalH : 0;
                const unreadH = totalH - readH;
                return (
                  <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div style={{ fontSize: 10, color: "#374151", marginBottom: 2 }}>{s.pct === null ? "—" : `${s.pct}%`}</div>
                    <div style={{ width: "70%", display: "flex", flexDirection: "column", justifyContent: "flex-end", height: 200 }}>
                      <div style={{ width: "100%", height: unreadH, background: "#ef4444" }}></div>
                      <div style={{ width: "100%", height: readH, background: "#10b981" }}></div>
                    </div>
                    <div style={{ fontSize: 10, color: "#6b7280", marginTop: 4, textAlign: "center" }}>{s.monthLabel}</div>
                  </div>
                );
              })}
            </div>
          )}

          {!loading && !error && series.length > 0 && (
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
              Each bar = the cohort of staff who should have read this SOP at the end of that month. Compliance is calculated against the active assignments at each historical snapshot.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



export default Mockupp;
export { NearMissAnalysis, SOPCompliance };